/**
 * Velocity JSON Generator
 *
 * Generates Velocity JSON configurations from mapped GeoEvent components.
 * Output format matches actual Velocity backup/export structure.
 */

import type { GeoEventConfiguration, Input, Output, GeoEventService, GeoEventDefinition, FieldDefinition, ArcGISServerConnection, FolderDataStore, ServiceNode } from '../types/geoevent.js';
import type {
  VelocityConfiguration,
  VelocityFeedItem,
  VelocityRealtimeAnalytic,
  AnalyticFeedRef,
  AnalyticSource,
  AnalyticTool,
  AnalyticOutput,
  PipelineConnection,
  PipelineEndpoint,
  SchemaAttribute,
  SchemaGeometry,
  FieldMapping,
  TargetPlatform,
} from '../types/velocity.js';
import type { ConfigurationMappingResult } from '../types/mapping.js';
import type { MigrationStrategy } from '../types/api.js';
import type { GenerationOptions } from '../types/generator.js';
import { mapConfiguration } from '../mapper/component-mapper.js';
import { lookupProcessorMapping, type LoadedFunctionalMatrix } from '../mapper/matrix-loader.js';
import { redactSensitiveObject, redactSensitiveText } from '../security/redaction.js';
import { sanitizeVelocityLabel } from './velocity-label.js';
import {
  convertGeoEventExpressionToArcade,
  findUnsupportedArcadeFunctions,
  stripAutoConvertedArcadeComment,
} from '../mapper/expression-converter.js';
import { translateRegexMatchToArcade } from '../mapper/regex-to-arcade.js';
import { v4 as uuidv4 } from 'uuid';

/**
 * Result of JSON generation
 */
export interface GenerationResult {
  success: boolean;
  configuration: VelocityConfiguration | null;
  mappingResult: ConfigurationMappingResult;
  warnings: string[];
  errors: string[];
}

// Cached matrix to avoid reloading
let cachedMatrix: LoadedFunctionalMatrix | null = null;

/**
 * Pluggable default-matrix loader. Node entry points register a loader
 * (see `json-generator-node.ts`) that reads the JSON from disk; browser
 * bundles must always pass `matrix` explicitly to `generateVelocityJson`
 * so this stays `null` and no Node-only modules end up in the bundle.
 */
type DefaultMatrixLoader = () => LoadedFunctionalMatrix;
let defaultMatrixLoader: DefaultMatrixLoader | null = null;
export function registerDefaultMatrixLoader(loader: DefaultMatrixLoader): void {
  defaultMatrixLoader = loader;
}

const SECRET_REDACTION_WARNING =
  'Sensitive credential-like values were redacted from generated artifacts. Re-enter passwords, tokens, API keys, authorization headers, OAuth secrets, and similar values manually in Velocity after import.';

/**
 * Returns the matrix to use during generation.
 *
 * Browser-safety note (Phase 5 of hybrid-proposal-strategy.md): browser
 * callers must pass `matrix` explicitly via `generateVelocityJson`.
 * Node callers can omit it and the matrix will be lazy-loaded from
 * disk via the loader registered by `json-generator-node.ts`.
 */
function getMatrix(matrix?: LoadedFunctionalMatrix): LoadedFunctionalMatrix {
  if (matrix) return matrix;
  if (cachedMatrix) return cachedMatrix;
  if (!defaultMatrixLoader) {
    throw new Error(
      'No functional matrix available. Browser callers must pass `matrix` ' +
        'explicitly to generateVelocityJson; Node callers must import ' +
        '`./json-generator-node.js` once at startup to register the default loader.',
    );
  }
  cachedMatrix = defaultMatrixLoader();
  return cachedMatrix;
}

/**
 * Clears the cached matrix (useful for testing)
 */
export function clearMatrixCache(): void {
  cachedMatrix = null;
}

function redactGeneratedArtifact<T>(artifact: T): { artifact: T; changed: boolean } {
  const redacted = redactSensitiveObject(artifact);
  return {
    artifact: redacted,
    changed: JSON.stringify(redacted) !== JSON.stringify(artifact),
  };
}

function addRedactionWarningIfNeeded(warnings: string[], changed: boolean): void {
  if (changed && !warnings.includes(SECRET_REDACTION_WARNING)) {
    warnings.push(SECRET_REDACTION_WARNING);
  }
}

/**
 * Generates a UUID for Velocity components
 */
function generateVelocityId(): string {
  return uuidv4().replace(/-/g, '');
}

/**
 * Maps GeoEvent field type to Velocity data type
 */
function mapFieldType(geoEventType: string): string {
  const typeMap: Record<string, string> = {
    'Integer': 'Int64',
    'Short': 'Int32',
    'Long': 'Int64',
    'Float': 'Float64',
    'Double': 'Float64',
    'String': 'String',
    'Date': 'Date',
    'Boolean': 'Boolean',
    'Geometry': 'Geometry',
  };
  return typeMap[geoEventType] || 'String';
}

/**
 * Maps GeoEvent geometry type to Velocity geometry type
 */
function mapGeometryType(geoEventGeomType?: string): string {
  if (!geoEventGeomType) return 'esriGeometryPoint';
  const geomMap: Record<string, string> = {
    'Point': 'esriGeometryPoint',
    'Polyline': 'esriGeometryPolyline',
    'Polygon': 'esriGeometryPolygon',
    'MultiPoint': 'esriGeometryMultipoint',
  };
  return geomMap[geoEventGeomType] || 'esriGeometryPoint';
}

/**
 * Maps GeoEvent tag names to Velocity tag names
 */
const GE_TO_VELOCITY_TAG: Record<string, string> = {
  'TRACK_ID': 'TRACK_ID',
  'TIME_START': 'START_TIME',
  'TIME_END': 'END_TIME',
  'GEOMETRY': 'GEOMETRY',
};

function mapTagName(geTagName: string): string {
  return GE_TO_VELOCITY_TAG[geTagName] || geTagName;
}

/**
 * Builds schema attributes from GeoEvent definition.
 * For non-delimited feeds, attributes use actual field names.
 */
function buildSchemaAttributes(geoDef: GeoEventDefinition | undefined): SchemaAttribute[] {
  if (!geoDef) return [];
  
  return geoDef.fieldDefinitions
    .filter(f => f.type !== 'Geometry')
    .map(field => ({
      name: field.name,
      dataType: mapFieldType(field.type),
      nullable: true,
      tags: (field.tags || []).map(t => mapTagName(t.name)),
    }));
}

/**
 * Builds delimited schema attributes using col_N column naming.
 * Delimited feeds use generic column names because Velocity assigns
 * them before field mappings rename them to actual names.
 */
function buildDelimitedSchemaAttributes(geoDef: GeoEventDefinition): SchemaAttribute[] {
  return geoDef.fieldDefinitions
    .filter(f => f.type !== 'Geometry')
    .map((field, i) => ({
      name: `col_${i + 1}`,
      dataType: mapFieldType(field.type),
      nullable: true,
      tags: (field.tags || []).map(t => mapTagName(t.name)),
    }));
}

/**
 * Builds field mappings from GeoEvent definition.
 * For non-delimited feeds, maps field names to themselves.
 */
function buildFieldMappings(geoDef: GeoEventDefinition | undefined): FieldMapping[] {
  if (!geoDef) return [];
  
  return geoDef.fieldDefinitions
    .filter(f => f.type !== 'Geometry')
    .map(field => ({
      fromField: field.name,
      toField: field.name,
      tags: (field.tags || []).map(t => mapTagName(t.name)),
    }));
}

/**
 * Builds delimited field mappings using col_N → actual field names.
 */
function buildDelimitedFieldMappings(geoDef: GeoEventDefinition): FieldMapping[] {
  return geoDef.fieldDefinitions
    .filter(f => f.type !== 'Geometry')
    .map((field, i) => ({
      fromField: `col_${i + 1}`,
      toField: field.name,
      tags: (field.tags || []).map(t => mapTagName(t.name)),
    }));
}

/**
 * Resolves a GeoEvent field name to its col_N reference for delimited feeds.
 */
function fieldNameToColRef(geoDef: GeoEventDefinition, fieldName: string): string {
  const nonGeomFields = geoDef.fieldDefinitions.filter(f => f.type !== 'Geometry');
  const idx = nonGeomFields.findIndex(f => f.name === fieldName);
  return idx >= 0 ? `col_${idx + 1}` : fieldName;
}

// ---------------------------------------------------------------------------
// Nested / array schema flattening
//
// GeoEvent Definitions can carry nested Group fields and Many-cardinality
// (array) groups. Velocity JSON feeds handle these by flattening at the feed
// level: nested objects become `parent_child` scalar fields and arrays of
// objects become `array_N_leaf` scalar fields (see
// https://doc.arcgis.com/en/velocity/ingest/JSON.htm). Un-flattened arrays
// arrive as opaque strings and cannot be indexed by Arcade, so flattening is
// the reliable path for downstream Map Fields references.
// ---------------------------------------------------------------------------

/** True when a field is a nested object group or a Many-cardinality array. */
function isNestedOrArrayField(field: FieldDefinition): boolean {
  return field.type === 'Group'
    || field.cardinality === 'Many'
    || (field.nestedFields?.length ?? 0) > 0;
}

/** True when a definition contains any nested object group or array field. */
function hasNestedOrArrayFields(geoDef: GeoEventDefinition | undefined): boolean {
  return !!geoDef && geoDef.fieldDefinitions.some(isNestedOrArrayField);
}

/** True when a definition contains any Many-cardinality (array) group field. */
function defHasArrayField(geoDef: GeoEventDefinition | undefined): boolean {
  return !!geoDef && geoDef.fieldDefinitions.some(
    f => f.cardinality === 'Many' && (f.nestedFields?.length ?? 0) > 0
  );
}

/**
 * Translates a GeoEvent field accessor path to the field name Velocity produces
 * when the feed flattens nested objects and arrays: `.` becomes `_` and `[N]`
 * becomes `_N`. Examples:
 *   position.longitude   -> position_longitude
 *   velocity.groundSpeed -> velocity_groundSpeed
 *   variables[0].value   -> variables_0_value
 */
function geoEventPathToFlattenedField(path: string): string {
  return path.replace(/\[(\d+)\]/g, '_$1').replace(/\./g, '_');
}

/**
 * Strips GeoEvent's double-quote field-reference wrapping from a Field Mapper
 * token (e.g. `"position.longitude"` -> `position.longitude`). Single-quoted
 * tokens are left intact so the expression converter can treat them as string
 * literals.
 */
function stripFieldQuotes(token: string): string {
  const trimmed = token.trim();
  const doubleQuoted = /^"(.*)"$/.exec(trimmed);
  return doubleQuoted ? (doubleQuoted[1] ?? trimmed) : trimmed;
}

/**
 * Parses a pure structural field accessor path — dot-separated identifiers with
 * optional `[n]` array indices — into a root field name plus the ordered keys
 * that follow. Returns null when the token is not a pure accessor (e.g. it
 * contains operators or function calls), signalling the caller to fall back to
 * general GeoEvent-expression conversion.
 */
function tokenizeFieldPath(
  path: string,
): { root: string; keys: Array<string | number>; hasArray: boolean } | null {
  if (!/^[A-Za-z_]\w*(\[\d+\])*(\.[A-Za-z_]\w*(\[\d+\])*)*$/.test(path)) {
    return null;
  }
  const tokens: Array<string | number> = [];
  let hasArray = false;
  const segment = /([A-Za-z_]\w*)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = segment.exec(path)) !== null) {
    if (match[1] !== undefined) {
      tokens.push(match[1]);
    } else if (match[2] !== undefined) {
      tokens.push(Number(match[2]));
      hasArray = true;
    }
  }
  const root = String(tokens[0] ?? path);
  return { root, keys: tokens.slice(1), hasArray };
}

/**
 * Builds an Arcade accessor that mirrors a nested/array GeoEvent path against a
 * feed that retained its nested structure (flatten toggle OFF). Object paths use
 * bracket member access; paths containing an array index use the Arcade
 * `DefaultValue(container, keys, default)` array function for safe drill-down.
 */
function structuralPathToNestedArcade(
  tokenized: { root: string; keys: Array<string | number>; hasArray: boolean },
): string {
  const rootExpr = `$feature[${JSON.stringify(tokenized.root)}]`;
  if (tokenized.keys.length === 0) return rootExpr;
  if (tokenized.hasArray) {
    const keyList = tokenized.keys
      .map(k => (typeof k === 'number' ? String(k) : JSON.stringify(k)))
      .join(', ');
    return `DefaultValue(${rootExpr}, [${keyList}], null)`;
  }
  return rootExpr + tokenized.keys.map(k => `[${JSON.stringify(k)}]`).join('');
}

/**
 * Resolves a GeoEvent field accessor path (used by filter operands) to an Arcade
 * `$feature` reference, applying the same flattening rules as the Map Fields
 * conversion so filter expressions reference the fields the feed actually emits.
 */
function operandToArcadeFieldRef(operand: string, doFlatten: boolean): string {
  const tokenized = tokenizeFieldPath(operand);
  if (tokenized && tokenized.keys.length > 0) {
    return doFlatten
      ? `$feature[${JSON.stringify(geoEventPathToFlattenedField(operand))}]`
      : structuralPathToNestedArcade(tokenized);
  }
  return `$feature[${JSON.stringify(operand)}]`;
}

/** Finds a nested leaf field definition by dotted path within a group's children. */
function findNestedLeafDefinition(
  fields: FieldDefinition[] | undefined,
  leafPath: string,
): FieldDefinition | undefined {
  if (!fields) return undefined;
  const [head, ...rest] = leafPath.split('.');
  const match = fields.find(f => f.name === head);
  if (!match) return undefined;
  return rest.length === 0 ? match : findNestedLeafDefinition(match.nestedFields, rest.join('.'));
}

/**
 * Recursively expands a nested object group into flattened leaf attributes using
 * Velocity's `parent_child` naming. Many-cardinality (array) groups are handled
 * separately because their element count is only known from sample data.
 */
function flattenObjectGroupLeaves(
  field: FieldDefinition,
  prefix: string,
): SchemaAttribute[] {
  const leaves: SchemaAttribute[] = [];
  for (const child of field.nestedFields ?? []) {
    if (child.type === 'Geometry') continue;
    const name = `${prefix}_${child.name}`;
    if (child.type === 'Group' && (child.nestedFields?.length ?? 0) > 0 && child.cardinality !== 'Many') {
      leaves.push(...flattenObjectGroupLeaves(child, name));
    } else if (child.cardinality !== 'Many') {
      leaves.push({
        name,
        dataType: mapFieldType(child.type),
        nullable: true,
        tags: (child.tags || []).map(t => mapTagName(t.name)),
      });
    }
  }
  return leaves;
}

/**
 * Scans every Field Mapper processor in the configuration for input tokens that
 * reference elements of the given array group (e.g. `variables[0].value`) and
 * returns the flattened attributes Velocity produces for them
 * (`variables_0_value`, ...). This keeps the flattened feed schema consistent
 * with the field names the generated Map Fields tools reference.
 */
function collectFlattenedArrayAttributes(
  arrayField: FieldDefinition,
  config: GeoEventConfiguration,
): SchemaAttribute[] {
  const attributes: SchemaAttribute[] = [];
  const seen = new Set<string>();
  const pattern = new RegExp(`^${arrayField.name}\\[(\\d+)\\](?:\\.(.+))?$`);
  for (const service of config.geoEventServices) {
    for (const node of service.nodes) {
      const uri = node.processor?.uri || '';
      if (!uri.includes('FieldMapper')) continue;
      const inputFields = splitProcessorFieldList(node.processor?.properties['inputFields']?.value);
      for (const token of inputFields) {
        const match = pattern.exec(stripFieldQuotes(token));
        if (!match) continue;
        const index = match[1];
        const leafPath = match[2];
        const flatName = leafPath
          ? `${arrayField.name}_${index}_${leafPath.replace(/\./g, '_')}`
          : `${arrayField.name}_${index}`;
        if (seen.has(flatName)) continue;
        seen.add(flatName);
        const leafDef = leafPath
          ? findNestedLeafDefinition(arrayField.nestedFields, leafPath)
          : undefined;
        attributes.push({
          name: flatName,
          dataType: leafDef ? mapFieldType(leafDef.type) : 'String',
          nullable: true,
          tags: [],
        });
      }
    }
  }
  return attributes;
}

/**
 * Builds the flattened Velocity feed schema for a definition that contains
 * nested objects and/or arrays. Scalar fields pass through unchanged, nested
 * object groups expand to `parent_child` leaves, and array groups expand to the
 * `array_N_leaf` fields referenced by downstream Field Mapper tools.
 */
function buildFlattenedSchemaAttributes(
  geoDef: GeoEventDefinition,
  config: GeoEventConfiguration,
): SchemaAttribute[] {
  const attributes: SchemaAttribute[] = [];
  const seen = new Set<string>();
  const push = (attr: SchemaAttribute): void => {
    if (seen.has(attr.name)) return;
    seen.add(attr.name);
    attributes.push(attr);
  };
  for (const field of geoDef.fieldDefinitions) {
    if (field.type === 'Geometry') continue;
    const isGroup = field.type === 'Group' && (field.nestedFields?.length ?? 0) > 0;
    if (isGroup && field.cardinality === 'Many') {
      for (const attr of collectFlattenedArrayAttributes(field, config)) push(attr);
    } else if (isGroup) {
      for (const attr of flattenObjectGroupLeaves(field, field.name)) push(attr);
    } else {
      push({
        name: field.name,
        dataType: mapFieldType(field.type),
        nullable: true,
        tags: (field.tags || []).map(t => mapTagName(t.name)),
      });
    }
  }
  return attributes;
}

/**
 * Resolves a folder data store GUID to its file system path.
 */
function resolveFolderPath(guid: string, folderDataStores: FolderDataStore[]): string {
  const store = folderDataStores.find(ds => ds.name === guid);
  return store?.path || guid;
}

/**
 * Gets the feed type name from GeoEvent input
 */
function getFeedTypeName(input: Input, targetPlatform: TargetPlatform = 'velocity-online'): string {
  const transportUri = input.transport?.uri || '';
  const connector = input.connector || '';
  const mode = String(input.transport?.properties?.mode?.value || input.transport?.properties?.MODE?.value || '').toUpperCase();
  
  if (transportUri.includes('FeatureService')) {
    return 'feature-layer';
  } else if (/\/File(?:\/|$)/.test(transportUri) && targetPlatform === 'velocity-enterprise') {
    return 'watch-file';
  } else if (transportUri.includes('HTTP')) {
    if (mode === 'SERVER' || /esri-receive-.*-rest-in/i.test(connector)) {
      return 'http-receiver';
    }
    return 'http-poller';
  } else if (transportUri.includes('WebSocket')) {
    return 'websocket';
  } else if (transportUri.includes('/TCP/') && targetPlatform === 'velocity-enterprise') {
    return 'tcp';
  } else if (transportUri.includes('Kafka')) {
    return 'kafka';
  } else if (transportUri.includes('MQTT')) {
    return 'mqtt';
  } else if (transportUri.includes('RSS') || transportUri.includes('rss')) {
    return 'rss-feed';
  } else if (transportUri.includes('StreamService') || transportUri.includes('Stream')) {
    return 'stream-layer';
  }
  
  return 'feature-layer'; // Default
}

/**
 * Resolves a datastore GUID to a feature service URL by looking up the
 * ArcGIS Server connection and constructing the full service URL.
 *
 * URL construction: {connection.url}rest/services/{serviceName}/{serviceType}/{layerIndex}
 *
 * AGOL caveat: when the resolved connection is effectively ArcGIS Online,
 * we cannot construct a usable feature service URL from `{portalUrl}/rest/services/...`
 * because hosted services live on a separate `services{N}.arcgis.com` host
 * that is only discoverable via a portal item lookup. In that case we
 * deliberately return an empty string so the caller can populate
 * `feature-layer.portalItemId` instead and emit a manual-action warning.
 */
function resolveFeatureServiceUrl(input: Input | Output, connections: ArcGISServerConnection[]): string {
  const tp = ('transport' in input) ? input.transport?.properties : undefined;
  if (!tp) return '';

  const customLayerUrl = tp['customLayerUrl']?.value || '';
  const datastoreGuid = tp['datastore']?.value || tp['dataStoreName']?.value || '';

  // If there's already a full URL in customLayerUrl, use it
  if (customLayerUrl && (customLayerUrl.startsWith('http://') || customLayerUrl.startsWith('https://'))) {
    return customLayerUrl;
  }

  // Resolve the datastore GUID to its connection base URL
  if (datastoreGuid && datastoreGuid !== 'default') {
    const conn = connections.find(c => c.name === datastoreGuid);
    if (conn) {
      // AGOL hosted services are not addressable via {portalUrl}/rest/services/...
      // Return empty so the feed falls back to portalItemId binding.
      if (conn.effectiveConnectionType === 'AGOL') {
        return '';
      }

      const baseUrl = conn.url.replace(/\/$/, '');

      // If customLayerUrl is provided, append it as a path
      if (customLayerUrl) {
        const urlPath = customLayerUrl.startsWith('/') ? customLayerUrl : `/${customLayerUrl}`;
        return `${baseUrl}${urlPath}`;
      }

      // Build the full feature service URL from transport properties:
      // {baseUrl}/rest/services/{serviceName}/{serviceType}/{layerIndex}
      const serviceName = String(tp['serviceName']?.value || '');
      const serviceType = String(tp['serviceType']?.value || 'FeatureServer');
      const layerIndex = String(tp['layerIndex']?.value ?? '0');

      if (serviceName) {
        return `${baseUrl}/rest/services/${serviceName}/${serviceType}/${layerIndex}`;
      }

      return conn.url;
    }
  }

  return customLayerUrl;
}

/**
 * Gets feed properties from GeoEvent input
 */
function getFeedProperties(
  input: Input,
  connections: ArcGISServerConnection[],
  targetPlatform: TargetPlatform = 'velocity-online',
  folderDataStores: FolderDataStore[] = [],
  warnings: string[] = [],
  geoDef?: GeoEventDefinition,
  flattenNestedSchema = true,
): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  const feedType = getFeedTypeName(input, targetPlatform);
  const formatName = getInputFormatName(input, geoDef);
  const doFlattenJson = flattenNestedSchema && hasNestedOrArrayFields(geoDef);
  
  if (input.transport?.properties) {
    const tp = input.transport.properties;
    
    if (feedType === 'feature-layer') {
      props[`${feedType}.portalItemId`] = String(tp['portalItemId']?.value || '');
      props[`${feedType}.layerId`] = Number(tp['layerIndex']?.value ?? 0);
      props[`${feedType}.outSR`] = tp['DefaultWKID']?.value || '4326';
      props[`${feedType}.query`] = tp['queryDefinition']?.value || '1=1';
      props[`${feedType}.enableRequestLogging`] = false;
      props[`${feedType}.url`] = resolveFeatureServiceUrl(input, connections);
      props[`${feedType}.fields`] = '*';

      // AGOL hosted services cannot be addressed via {portalUrl}/rest/services/...
      // When the source connection is effectively AGOL, warn the operator that
      // the feed must be bound by Portal Item ID in the Velocity Manager UI
      // before the import will produce a working pipeline.
      const datastoreGuid = String(tp['datastore']?.value || '');
      const sourceConn = datastoreGuid
        ? connections.find(c => c.name === datastoreGuid)
        : undefined;
      if (sourceConn?.effectiveConnectionType === 'AGOL'
          && !String(tp['portalItemId']?.value || '')) {
        const svcName = String(tp['serviceName']?.value || '(unknown)');
        const svcType = String(tp['serviceType']?.value || 'FeatureServer');
        const layerIdx = String(tp['layerIndex']?.value ?? '0');
        const declared = sourceConn.connectionType
          && sourceConn.connectionType.toUpperCase() !== 'AGOL'
          ? ` (registered as connectionType="${sourceConn.connectionType}")`
          : '';
        warnings.push(
          `Input "${input.label}": Source connection "${sourceConn.label}" at ${sourceConn.url} ` +
          `is an ArcGIS Online organization${declared}. ArcGIS Online hosted feature ` +
          `services are not addressable via {portalUrl}/rest/services/... — the actual ` +
          `service lives on a per-org services{N}.arcgis.com host that is only ` +
          `discoverable via a Portal item lookup. Before importing, locate the ` +
          `Portal item for "${svcName}" / ${svcType} / layer ${layerIdx} and set ` +
          `feature-layer.portalItemId in the Velocity Manager UI. If the service is ` +
          `secured, wrap it in a Portal item that stores credentials first ` +
          `(see https://enterprise.arcgis.com/en/portal/latest/use/arcgis-server-services.htm).`
        );
      }
      // G-4 AC#3: timestampField from GeoEvent adapter
      const tsField = String(tp['timestampField']?.value || '');
      if (tsField) {
        props[`${feedType}.timestampField`] = tsField;
      }
      // G-24: Spatial extent filter from GeoEvent bounding box
      const extentXmin = tp['geometryXmin']?.value ?? tp['extentXmin']?.value;
      const extentYmin = tp['geometryYmin']?.value ?? tp['extentYmin']?.value;
      const extentXmax = tp['geometryXmax']?.value ?? tp['extentXmax']?.value;
      const extentYmax = tp['geometryYmax']?.value ?? tp['extentYmax']?.value;
      if (extentXmin != null && extentYmin != null && extentXmax != null && extentYmax != null) {
        const wkid = parseInt(String(tp['geometryWKID']?.value || tp['DefaultWKID']?.value || '4326'), 10);
        props[`${feedType}.extent`] = {
          spatialReference: { wkid },
          xmin: Number(extentXmin),
          ymin: Number(extentYmin),
          xmax: Number(extentXmax),
          ymax: Number(extentYmax),
        };
      }
    } else if (feedType === 'http-poller') {
      props[`${feedType}.url`] = String(tp['url']?.value || '');
      props[`${feedType}.httpMethod`] = String(tp['httpMethod']?.value || 'GET');
      props[`${feedType}.httpAuthenticationType`] = 'noauth';
      props[`${feedType}.enableRequestLogging`] = false;
      props[`${feedType}.isLongPolling`] = false;
      props[`${feedType}.globalDatetimeFormat`] = 'epoch_millis';
      // G-23: Custom headers (e.g., Authorization, Content-Type)
      const pollerHeaderStr = String(tp['httpHeaders']?.value || '');
      if (pollerHeaderStr) {
        const pollerHeaders: Record<string, string> = {};
        pollerHeaderStr.split(';').forEach(h => {
          const [k, ...v] = h.split(':');
          if (k && v.length) pollerHeaders[k.trim()] = v.join(':').trim();
        });
        if (Object.keys(pollerHeaders).length > 0) {
          props[`${feedType}.headers`] = pollerHeaders;
        }
      }
      // G-23: URL query parameters
      const pollerUrlParams = String(tp['urlParameters']?.value || '');
      if (pollerUrlParams) {
        const pollerParams: Record<string, string> = {};
        pollerUrlParams.split('&').forEach(p => {
          const [k, ...v] = p.split('=');
          if (k && v.length) pollerParams[k.trim()] = v.join('=').trim();
        });
        if (Object.keys(pollerParams).length > 0) {
          props[`${feedType}.urlParameters`] = pollerParams;
        }
      }
    } else if (feedType === 'http-receiver') {
      props[`${feedType}.httpAuthenticationType`] = 'none';
      props[`${feedType}.enableRequestLogging`] = false;
    } else if (feedType === 'websocket') {
      props[`${feedType}.url`] = String(tp['url']?.value || tp['webSocketURL']?.value || '');
      // WebSocket headers for authentication (e.g., API keys)
      const wsHeaders: Record<string, string> = {};
      if (tp['httpHeaders']?.value) {
        // GeoEvent stores headers as a string; attempt to parse key:value pairs
        const headerStr = String(tp['httpHeaders'].value);
        if (headerStr) {
          headerStr.split(';').forEach(h => {
            const [k, ...v] = h.split(':');
            if (k && v.length) wsHeaders[k.trim()] = v.join(':').trim();
          });
        }
      }
      if (Object.keys(wsHeaders).length > 0) {
        props[`${feedType}.headers`] = wsHeaders;
      }
    } else if (feedType === 'tcp') {
      props[`${feedType}.host`] = String(tp['host']?.value || '');
      props[`${feedType}.port`] = Number(tp['port']?.value ?? 5570);
    } else if (feedType === 'kafka') {
      props[`${feedType}.brokers`] = String(tp['bootstrapServers']?.value || tp['kafkaServers']?.value || '');
      props[`${feedType}.topics`] = String(tp['topic']?.value || '');
      props[`${feedType}.authenticationType`] = 'None';
      props[`${feedType}.useSSL`] = false;
      props[`${feedType}.username`] = '';
      props[`${feedType}.password`] = '';
    } else if (feedType === 'mqtt') {
      props[`${feedType}.host`] = String(tp['mqttHost']?.value || tp['host']?.value || '');
      props[`${feedType}.port`] = Number(tp['mqttPort']?.value ?? tp['port']?.value ?? 1883);
      props[`${feedType}.topic`] = String(tp['mqttTopic']?.value || tp['topic']?.value || '');
      props[`${feedType}.qos`] = 0;
      props[`${feedType}.username`] = String(tp['mqttUsername']?.value || tp['username']?.value || '');
      props[`${feedType}.password`] = '';
      const clientId = String(tp['mqttClientId']?.value || tp['clientId']?.value || '');
      if (clientId) {
        props[`${feedType}.clientid`] = clientId;
      }
    } else if (feedType === 'rss-feed') {
      props[`${feedType}.url`] = String(tp['url']?.value || '');
      props[`${feedType}.httpAuthenticationType`] = 'noauth';
      props[`${feedType}.enableRequestLogging`] = false;
      // G-22: RSS date format properties — determined by whether geometry is present
      const hasGeom = geoDef?.fieldDefinitions.some(f => f.type === 'Geometry');
      if (hasGeom) {
        props['geo-rss-format.dateFormat'] = 'epoch_millis';
      } else {
        props['rss-format.dateFormat'] = 'epoch_millis';
      }
    } else if (feedType === 'stream-layer') {
      props[`${feedType}.portalItemId`] = String(tp['portalItemId']?.value || '');
      props[`${feedType}.outSR`] = tp['DefaultWKID']?.value || '4326';
      props[`${feedType}.fields`] = '*';
      props[`${feedType}.query`] = tp['queryDefinition']?.value || '';
    } else if (feedType === 'watch-file') {
      // Resolve folder data store GUID to file system path
      const folderGuid = String(tp['inputFolderDataStore']?.value || '');
      props[`${feedType}.fileDirectory`] = resolveFolderPath(folderGuid, folderDataStores);

      props[`${feedType}.fileFilter`] = String(tp['inputFileFilter']?.value || '*');
      props[`${feedType}.deleteFiles`] = tp['inputDeleteFiles']?.value === 'true';

      // Clamp linesBatchInterval to Velocity minimum of 1000ms
      const rawInterval = parseInt(String(tp['inputBatchFlushIntervalInMS']?.value || '1000'), 10);
      const clampedInterval = Math.max(rawInterval, 1000);
      props[`${feedType}.linesBatchInterval`] = clampedInterval;
      if (rawInterval > 0 && rawInterval < 1000) {
        warnings.push(
          `Input "${input.label}": Batch interval was ${rawInterval}ms, adjusted to ${clampedInterval}ms to meet Velocity minimum of 1000ms.`
        );
      }

      props[`${feedType}.readLines`] = tp['inputIsTextFile']?.value === 'true';
      const linesPerBatch = parseInt(String(tp['inputBatchMaxLinesCount']?.value || '1'), 10);
      props[`${feedType}.linesPerBatch`] = Math.max(linesPerBatch, 1);

      // Generate format properties from adapter
      if (input.adapter?.properties) {
        const ap = input.adapter.properties;
        if (formatName === 'delimited') {
          const separator = String(ap['AttributeSeparator']?.value || ',');
          // Use actual newline character, not escaped string
          const msgSep = String(ap['MessageSeparator']?.value || '\n');
          const recordTerminator = msgSep === '\\n' ? '\n' : msgSep;

          props['delimited.fieldDelimiter'] = separator;
          props['delimited.recordTerminator'] = recordTerminator;
          props['delimited.hasHeaderRow'] = false;

          // Date format — use CustomDateFormat if set, otherwise provide ISO default
          const customDate = String(ap['CustomDateFormat']?.value || '');
          if (customDate) {
            props['delimited.dateFormat'] = customDate;
          } else {
            props['delimited.dateFormat'] = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
          }

          // Geometry from fields — use col_N references when geoDef is available
          if (ap['BuildGeometryFromFields']?.value === 'true') {
            props['delimited.buildGeometryFromFields'] = true;

            const xFieldName = String(ap['XGeometryField']?.value || '');
            const yFieldName = String(ap['YGeometryField']?.value || '');
            const zFieldName = String(ap['ZGeometryField']?.value || '');

            if (geoDef) {
              // Map field names to col_N references for delimited format
              props['delimited.xField'] = fieldNameToColRef(geoDef, xFieldName);
              props['delimited.yField'] = fieldNameToColRef(geoDef, yFieldName);
            } else {
              props['delimited.xField'] = xFieldName;
              props['delimited.yField'] = yFieldName;
            }

            if (zFieldName) {
              props['delimited.hasZField'] = true;
              props['delimited.zField'] = geoDef ? fieldNameToColRef(geoDef, zFieldName) : zFieldName;
              // zUnit 9003 = international feet (common GeoEvent default)
              props['delimited.zUnit'] = 9003;
            }
          }
        } else if (formatName === 'json') {
          const buildGeom = ap['BuildGeometryFromFields']?.value === 'true';
          props['json.buildGeometryFromFields'] = buildGeom;
          if (buildGeom) {
            const xField = String(ap['XGeometryField']?.value || '');
            const yField = String(ap['YGeometryField']?.value || '');
            props['json.xField'] = doFlattenJson ? geoEventPathToFlattenedField(xField) : xField;
            props['json.yField'] = doFlattenJson ? geoEventPathToFlattenedField(yField) : yField;
          }
          // Flatten nested objects/arrays so downstream Map Fields tools can
          // reference the resulting scalar fields.
          if (doFlattenJson) {
            props['json.flatten'] = true;
            if (defHasArrayField(geoDef)) props['json.flattenArrays'] = true;
          }

          const customDate = String(ap['CustomDateFormat']?.value || '');
          if (customDate) {
            props['json.dateFormat'] = customDate;
          } else {
            props['json.dateFormat'] = 'epoch_millis';
          }
        }
      }
    }
  }

  // Generate format-specific properties for non-watch-file feeds (G-15 through G-18)
  // Watch-file feeds already handle delimited properties above.
  if (feedType !== 'watch-file' && input.adapter?.properties) {
    const ap = input.adapter.properties;
    const adapterUri = input.adapter?.uri || '';

    // Determine format from the same centralized inference used for feed.formatName.
    let format: string | undefined = formatName;
    if (!format && (adapterUri.includes('/Text/') || adapterUri.includes('/CSV/'))) {
      format = 'delimited';
    } else if (!format && (adapterUri.includes('/Generic-JSON/') || adapterUri.includes('/JSON/') || adapterUri.includes('/Json/'))) {
      format = 'json';
    } else if (!format && (adapterUri.includes('/GeoJSON/') || adapterUri.includes('/GeoJson/'))) {
      format = 'geo-json';
    } else if (!format && (adapterUri.includes('/XML/') || adapterUri.includes('/Xml/'))) {
      format = 'xml';
    } else if (!format && (adapterUri.includes('/EsriJSON/') || adapterUri.includes('/EsriJson/'))) {
      format = 'esri-json';
    }

    if (format === 'json') {
      // JSON format properties (G-15)
      const buildGeom = ap['BuildGeometryFromFields']?.value === 'true';
      props['json.buildGeometryFromFields'] = buildGeom;
      if (buildGeom) {
        const xField = String(ap['XGeometryField']?.value || '');
        const yField = String(ap['YGeometryField']?.value || '');
        props['json.xField'] = doFlattenJson ? geoEventPathToFlattenedField(xField) : xField;
        props['json.yField'] = doFlattenJson ? geoEventPathToFlattenedField(yField) : yField;
      }
      // Flatten nested objects/arrays so downstream Map Fields tools can
      // reference the resulting scalar fields (position_longitude, variables_0_value).
      if (doFlattenJson) {
        props['json.flatten'] = true;
        if (defHasArrayField(geoDef)) props['json.flattenArrays'] = true;
      }
      // Geometry field format for single-field geometry
      const geomField = String(ap['GeometryField']?.value || '');
      if (geomField) {
        props['json.geometryField'] = geomField;
        props['json.geometryFieldFormat'] = String(ap['GeometryFieldFormat']?.value || 'esrijson');
      }
      // Date format
      const customDate = String(ap['CustomDateFormat']?.value || '');
      if (customDate) {
        props['json.dateFormat'] = customDate;
      } else {
        props['json.dateFormat'] = 'epoch_millis';
      }
    } else if (format === 'delimited') {
      // Delimited format properties for non-watch-file feeds (G-16)
      const separator = String(ap['AttributeSeparator']?.value || ',');
      const msgSep = String(ap['MessageSeparator']?.value || '\n');
      const recordTerminator = msgSep === '\\n' ? '\n' : msgSep;

      props['delimited.fieldDelimiter'] = separator;
      props['delimited.recordTerminator'] = recordTerminator;
      props['delimited.hasHeaderRow'] = ap['hasHeaderRow']?.value === 'true';

      const customDate = String(ap['CustomDateFormat']?.value || '');
      props['delimited.dateFormat'] = customDate || 'epoch_millis';

      if (ap['BuildGeometryFromFields']?.value === 'true') {
        props['delimited.buildGeometryFromFields'] = true;
        props['delimited.xField'] = String(ap['XGeometryField']?.value || '');
        props['delimited.yField'] = String(ap['YGeometryField']?.value || '');
        const zFieldName2 = String(ap['ZGeometryField']?.value || '');
        if (zFieldName2) {
          props['delimited.hasZField'] = true;
          props['delimited.zField'] = zFieldName2;
          props['delimited.zUnit'] = 9003;
        }
      }
    } else if (format === 'xml') {
      // XML format properties (G-17)
      const buildGeom = ap['BuildGeometryFromFields']?.value === 'true';
      props['xml.buildGeometryFromFields'] = buildGeom;
      if (buildGeom) {
        props['xml.xField'] = String(ap['XGeometryField']?.value || '');
        props['xml.yField'] = String(ap['YGeometryField']?.value || '');
      }
      const zFieldName2 = String(ap['ZGeometryField']?.value || '');
      if (zFieldName2) {
        props['xml.hasZField'] = true;
      } else {
        props['xml.hasZField'] = false;
      }
      const rootNode = String(ap['RootNode']?.value || '');
      if (rootNode) {
        props['xml.rootNode'] = rootNode;
      }
      props['xml.flatten'] = true;
      const customDate = String(ap['CustomDateFormat']?.value || '');
      props['xml.dateFormat'] = customDate || "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
    } else if (format === 'geo-json') {
      // GeoJSON format properties (G-18)
      const customDate = String(ap['CustomDateFormat']?.value || '');
      if (customDate) {
        props['geo-json.dateFormat'] = customDate;
      }
    } else if (format === 'esri-json') {
      // Esri JSON format properties (G-18)
      const customDate = String(ap['CustomDateFormat']?.value || '');
      if (customDate) {
        props['esri-json.dateFormat'] = customDate;
      }
    }
  }
  
  return props;
}

/**
 * Gets recurrence expression from GeoEvent input.
 * Converts refreshInterval (in seconds) to a Quartz Scheduler cron expression.
 * Quartz format: seconds minutes hours dayOfMonth month dayOfWeek year
 */
function getRecurrenceExpression(input: Input): string {
  const refreshInterval = input.transport?.properties?.['refreshInterval']?.value;
  const seconds = typeof refreshInterval === 'string' ? parseInt(refreshInterval, 10) : (typeof refreshInterval === 'number' ? refreshInterval : 10);
  const interval = isNaN(seconds) || seconds <= 0 ? 10 : Math.max(seconds, 10);

  if (interval >= 3600 && interval % 3600 === 0) {
    // Express as hours
    const hours = interval / 3600;
    return `0 0 0/${hours} ? * * *`;
  } else if (interval >= 60) {
    // Express as minutes
    const minutes = Math.round(interval / 60);
    return `0 0/${minutes} * ? * * *`;
  }
  // Express as seconds
  return `0/${interval} * * ? * * *`;
}

function getInputFormatName(input: Input, geoDef?: GeoEventDefinition): string | undefined {
  const adapterUri = input.adapter?.uri || '';
  if (adapterUri.includes('/Text/') || adapterUri.includes('/CSV/')) {
    return 'delimited';
  } else if (adapterUri.includes('/Generic-JSON/') || adapterUri.includes('/JSON/') || adapterUri.includes('/Json/')) {
    return 'json';
  } else if (adapterUri.includes('/GeoJSON/') || adapterUri.includes('/GeoJson/')) {
    return 'geo-json';
  } else if (adapterUri.includes('/RSS/') || adapterUri.includes('/rss/')) {
    const hasGeometry = geoDef?.fieldDefinitions.some(f => f.type === 'Geometry');
    return hasGeometry ? 'geo-rss-format' : 'rss-format';
  } else if (adapterUri.includes('/XML/') || adapterUri.includes('/Xml/')) {
    return 'xml';
  }

  const connector = input.connector?.toLowerCase() || '';
  if (connector.includes('csv')) return 'delimited';
  if (connector.includes('geojson')) return 'geo-json';
  if (connector.includes('rss')) {
    const hasGeometry = geoDef?.fieldDefinitions.some(f => f.type === 'Geometry');
    return hasGeometry ? 'geo-rss-format' : 'rss-format';
  }
  if (connector.includes('xml')) return 'xml';
  if (connector.includes('json')) return 'json';
  return undefined;
}

/**
 * Converts GeoEvent input to Velocity feed item
 */
function inputToVelocityFeed(
  input: Input,
  geoDef: GeoEventDefinition | undefined,
  idMap: Map<string, string>,
  connections: ArcGISServerConnection[],
  targetPlatform: TargetPlatform = 'velocity-online',
  folderDataStores: FolderDataStore[] = [],
  warnings: string[] = [],
  config?: GeoEventConfiguration,
  flattenNestedSchema = true,
): VelocityFeedItem {
  const feedId = generateVelocityId();
  idMap.set(input.name, feedId);
  
  const feedType = getFeedTypeName(input, targetPlatform);
  const formatName = getInputFormatName(input, geoDef);
  const isDelimited = formatName === 'delimited';
  const isWatchFile = feedType === 'watch-file';

  // G-26: Detect non-watch-file feeds with delimited format — these use actual field names
  // (not col_N generics) based on reference examples. Add warning for review.
  const adapterUriForFormat = input.adapter?.uri || '';
  const isNonWatchFileDelimited = !isWatchFile &&
    (adapterUriForFormat.includes('/Text/') || adapterUriForFormat.includes('/CSV/'));
  if (isNonWatchFileDelimited) {
    warnings.push(
      `Input "${input.label}": Non-watch-file delimited feed — using actual field names in schema. If import fails, col_N naming may be required.`
    );
  }

  // Sanitize label for Velocity validation
  const rawLabel = input.label || input.name;
  const { sanitized: label, wasModified } = sanitizeVelocityLabel(rawLabel);
  if (wasModified) {
    warnings.push(
      `Input "${rawLabel}": Label was adjusted to "${label}" to meet Velocity naming rules (no spaces or special characters).`
    );
  }

  // Build schema — delimited feeds use col_N column naming
  const doFlatten = flattenNestedSchema && hasNestedOrArrayFields(geoDef);
  let attributes: SchemaAttribute[];
  let fieldMappings: FieldMapping[];
  if (isDelimited && geoDef) {
    attributes = buildDelimitedSchemaAttributes(geoDef);
    fieldMappings = buildDelimitedFieldMappings(geoDef);
  } else if (doFlatten && geoDef && config) {
    // Nested objects and arrays are flattened at the feed level so downstream
    // Map Fields tools can reference the resulting scalar fields.
    attributes = buildFlattenedSchemaAttributes(geoDef, config);
    fieldMappings = attributes.map(attr => ({
      fromField: attr.name,
      toField: attr.name,
      tags: attr.tags,
    }));
  } else {
    attributes = buildSchemaAttributes(geoDef);
    fieldMappings = buildFieldMappings(geoDef);
  }
  
  // Build geometry if present
  let geometry: SchemaGeometry | undefined;
  const geomField = geoDef?.fieldDefinitions.find(f => f.type === 'Geometry');
  if (geomField) {
    // Prefer SpatialReferenceField (used by file adapters), fall back to DefaultWKID
    const wkidStr = input.adapter?.properties?.['SpatialReferenceField']?.value as string
      || input.adapter?.properties?.['DefaultWKID']?.value as string
      || '4326';
    const wkid = parseInt(wkidStr, 10) || 4326;

    // Check for Z geometry field
    const hasZ = !!input.adapter?.properties?.['ZGeometryField']?.value;

    geometry = {
      geometryType: mapGeometryType(),
      spatialReference: {
        wkid,
      },
      hasZ,
      hasM: false,
    };
  }

  // Build schema transformation
  let schemaTransformation: VelocityFeedItem['feed']['schemaTransformation'];
  if (attributes.length > 0) {
    // Check if any Date field has a TIME_START/START_TIME tag, or if any Date field exists.
    // Velocity requires time metadata when the schema has temporal fields; cert tests
    // always include `time: { timeType: 'instant' }` even without explicit tags.
    const hasTimeField = geoDef?.fieldDefinitions.some(
      f => f.tags?.some(t => t.name === 'TIME_START' || t.name === 'TIME_END')
    );
    const hasDateAttribute = attributes.some(a => a.dataType === 'Date');

    schemaTransformation = {
      inputSchema: {
        attributes,
        geometry,
        ...((hasTimeField || hasDateAttribute) ? { time: { timeType: 'instant' } } : {}),
      },
      fieldMappings,
    };
  }

  // Watch-file feeds are event-driven; only polling feeds get recurrence
  const recurrence = isWatchFile ? undefined : {
    expression: getRecurrenceExpression(input),
    timeZone: 'America/New_York',
    suspend: false,
  };

  // Surface how nested/array schemas were handled so the operator knows what to
  // confirm in Velocity after import.
  if (!isDelimited && hasNestedOrArrayFields(geoDef)) {
    if (doFlatten) {
      const flags = defHasArrayField(geoDef)
        ? '`json.flatten` and `json.flattenArrays`'
        : '`json.flatten`';
      warnings.push(
        `Input "${input.label}": nested/array fields were flattened for Velocity (${flags} enabled). ` +
        `Downstream Map Fields references the flattened scalar field names produced by Velocity's flatten options. ` +
        `Confirm the derived flattened schema when creating the feed.`
      );
    } else {
      warnings.push(
        `Input "${input.label}": nested/array fields were kept un-flattened to mirror GeoEvent. In Velocity, ` +
        `un-flattened arrays (and exempted objects) arrive as strings, so the Map Fields expressions for those ` +
        `fields will not resolve until you enable flattening on the feed or parse the strings.`
      );
    }
  }

  return {
    id: feedId,
    label,
    description: '',
    feed: {
      name: feedType,
      formatName,
      schemaTransformation,
      properties: getFeedProperties(input, connections, targetPlatform, folderDataStores, warnings, geoDef, flattenNestedSchema),
    },
    recurrence,
    properties: {
      executable: true,
      folderName: null,
    },
    shareProperties: false,
  };
}

/**
 * Gets the output type name from GeoEvent output.
 * Maps to actual Velocity output type IDs from source code.
 * When migrationStrategy is 'in-place', all feature layer outputs
 * use feat-lyr-existing (reference existing layers) instead of feat-lyr-new.
 */
function getOutputTypeName(
  output: Output,
  migrationStrategy: MigrationStrategy = 'side-by-side',
  targetPlatform: TargetPlatform = 'velocity-online'
): string {
  const transportUri = output.transport?.uri || '';
  const connectorId = output.connector || '';
  const transportUriLower = transportUri.toLowerCase();
  const connectorIdLower = connectorId.toLowerCase();

  if (
    targetPlatform === 'velocity-enterprise' &&
    ['esri-out-csv-file-log', 'esri-out-geojson-file-log', 'esri-out-json-file-log'].includes(connectorId)
  ) {
    return 'write-file';
  }
  
  // Check connector ID first for more accurate mapping
  if (connectorId.includes('fs-update')) {
    return 'feat-lyr-existing';
  } else if (connectorId.includes('fs-add') || connectorId.includes('bds-add')) {
    return migrationStrategy === 'in-place' ? 'feat-lyr-existing' : 'feat-lyr-new';
  } else if (connectorId.includes('bds-update')) {
    return 'feat-lyr-existing';
  } else if (connectorIdLower.includes('json-http-external') || connectorIdLower.includes('http-external')) {
    return 'http-output';
  }
  
  // Fall back to transport URI matching
  if (transportUri.includes('FeatureService')) {
    return migrationStrategy === 'in-place' ? 'feat-lyr-existing' : 'feat-lyr-new';
  } else if (transportUri.includes('StreamService') || transportUri.includes('Stream')) {
    return 'stream-lyr-new';
  } else if (transportUri.includes('Kafka')) {
    return 'kafka';
  } else if (transportUri.includes('SMTP')) {
    return 'email';
  } else if (transportUri.includes('SMS')) {
    return 'text-message';
  } else if (transportUri.includes('WebSocket')) {
    return 'http-output';
  } else if (transportUriLower.includes('outbound/http') || transportUriLower.includes('/http')) {
    return 'http-output';
  } else if (transportUri.includes('BDS')) {
    return 'feat-lyr-new';
  }
  
  return 'feat-lyr-new'; // Default
}

/**
 * Gets output properties from GeoEvent output
 */
function getOutputProperties(
  output: Output,
  ui: { left: number; top: number },
  _connections: ArcGISServerConnection[],
  migrationStrategy: MigrationStrategy = 'side-by-side',
  targetPlatform: TargetPlatform = 'velocity-online',
  folderDataStores: FolderDataStore[] = []
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    ui: {
      width: 240,
      height: 70,
      left: ui.left,
      top: ui.top,
    },
  };
  
  const outputType = getOutputTypeName(output, migrationStrategy, targetPlatform);
  
  if (output.transport?.properties) {
    const tp = output.transport.properties;
    
    if (outputType === 'feat-lyr-new') {
      props[`${outputType}.replaceFeatures`] = false;
      props[`${outputType}.updateExistingFeatures`] = tp['updateOnly']?.value === 'true';
      props[`${outputType}.editorTrackingEnabled`] = false;
      props[`${outputType}.name`] = tp['serviceName']?.value || output.label || output.name;
      // Default to relational (non-spatiotemporal) storage — safer default that works
      // universally. Spatiotemporal requires additional aggregationStyles configuration
      // and a registered spatiotemporal data store. Users migrating from GeoEvent BDS
      // outputs can opt into STBDS via the migration guide.
      const isBdsOutput = (output.connector || '').includes('bds');
      props[`${outputType}.useSpatiotemporal`] = isBdsOutput;
      if (isBdsOutput) {
        props[`${outputType}.dataRetentionStrategy`] = 'NoPurge';
        props[`${outputType}.dataRetentionExportStrategy`] = 'NoExport';
        props[`${outputType}.aggregationStyles`] = [
          { lods: 12, style: 'geohash', sr: '4326' },
          { lods: 30, style: 'square', sr: '102100' },
          { lods: 30, style: 'pointyTriangle', sr: '102100' },
        ];
      }
    } else if (outputType === 'feat-lyr-existing') {
      const portalItemId = String(tp['portalItemId']?.value || '');
      props[`${outputType}.portalItemId`] = portalItemId;
      props[`${outputType}.layerId`] = Number(tp['layerIndex']?.value ?? 0);
      props[`${outputType}.updateExistingFeatures`] = tp['updateOnly']?.value === 'true';
      props[`${outputType}.enableRequestLogging`] = false;
    } else if (outputType === 'stream-lyr-new') {
      props[`${outputType}.name`] = tp['serviceName']?.value || output.label || output.name;
      props[`${outputType}.keepLatest`] = true;
    } else if (outputType === 'kafka') {
      props[`${outputType}.brokers`] = String(tp['bootstrapServers']?.value || tp['kafkaServers']?.value || '');
      props[`${outputType}.topics`] = String(tp['topic']?.value || '');
      props[`${outputType}.authenticationType`] = 'None';
      props[`${outputType}.useSSL`] = false;
      // Note: formatName 'json' is set on the output object, not in properties
    } else if (outputType === 'email') {
      props[`${outputType}.smtpServer`] = String(tp['smtpHost']?.value || '');
      props[`${outputType}.port`] = Number(tp['smtpPort']?.value ?? 587);
      props[`${outputType}.smtpServerSecurity`] = 'STARTTLS';
      props[`${outputType}.username`] = String(tp['smtpUsername']?.value || '');
      props[`${outputType}.password`] = '';
      props[`${outputType}.fromEmailAddress`] = String(tp['fromAddress']?.value || '');
      props[`${outputType}.toEmailAddress`] = String(tp['toAddress']?.value || '');
      props[`${outputType}.subject`] = String(tp['subject']?.value || 'Velocity Alert');
      props[`${outputType}.messageBody`] = String(tp['messageBody']?.value || '');
    } else if (outputType === 'text-message') {
      props[`${outputType}.smtpServer`] = String(tp['smtpHost']?.value || '');
      props[`${outputType}.port`] = Number(tp['smtpPort']?.value ?? 587);
      props[`${outputType}.smtpServerSecurity`] = 'STARTTLS';
      props[`${outputType}.username`] = String(tp['smtpUsername']?.value || '');
      props[`${outputType}.password`] = '';
      props[`${outputType}.fromAddress`] = String(tp['fromAddress']?.value || '');
      props[`${outputType}.toAddress`] = String(tp['toAddress']?.value || '');
      props[`${outputType}.subject`] = String(tp['subject']?.value || '');
      props[`${outputType}.messageBody`] = String(tp['messageBody']?.value || '');
    } else if (outputType === 'http-output') {
      props[`${outputType}.url`] = String(tp['clientURL']?.value || tp['url']?.value || '');
      props[`${outputType}.httpMethod`] = String(tp['httpMethod']?.value || 'POST');
      props[`${outputType}.httpAuthenticationType`] = 'noauth';
      props[`${outputType}.contentType`] = String(tp['postContentType']?.value || tp['contentType']?.value || 'application/json');
      props[`${outputType}.enableRequestLogging`] = false;
      // HTTP request body — GeoEvent may have a message template
      const postBody = String(tp['messageBody']?.value || tp['postBody']?.value || '');
      if (postBody) {
        props[`${outputType}.postBody`] = postBody;
      }
      // Custom headers
      const headerStr = String(tp['headers']?.value || tp['httpHeaders']?.value || '');
      if (headerStr) {
        const headers: Record<string, string> = {};
        headerStr.split(/[;\n]+/).forEach(h => {
          const [k, ...v] = h.split(':');
          if (k && v.length) headers[k.trim()] = v.join(':').trim();
        });
        if (Object.keys(headers).length > 0) {
          props[`${outputType}.headers`] = headers;
        }
      }
      // URL parameters
      const urlParams = String(tp['clientParameters']?.value || tp['urlParameters']?.value || '');
      if (urlParams) {
        const params: Record<string, string> = {};
        urlParams.split(/[&;\n]+/).forEach(p => {
          const [k, ...v] = p.split('=');
          if (k && v.length) params[k.trim()] = v.join('=').trim();
        });
        if (Object.keys(params).length > 0) {
          props[`${outputType}.urlParameters`] = params;
        }
      }
      // G-25: OAuth token configuration
      const tokenUrl = String(tp['oAuthTokenUrl']?.value || tp['tokenUrl']?.value || '');
      if (tokenUrl) {
        props[`${outputType}.httpAuthenticationType`] = 'oauth';
        props[`${outputType}.accessTokenConfig`] = {
          tokenRequest: {
            url: tokenUrl,
            httpMethod: String(tp['oAuthHttpMethod']?.value || 'POST'),
            headers: {},
          },
          tokenResponse: {
            accessTokenField: String(tp['oAuthTokenField']?.value || 'access_token'),
          },
        };
        const tokenBody = String(tp['oAuthPostBody']?.value || '');
        if (tokenBody) {
          props[`${outputType}.accessTokenConfigPostBody`] = tokenBody;
        }
      }
    } else if (outputType === 'write-file') {
      const adapterProps = output.adapter?.properties || {};
      const folderGuid = String(tp['outputFolderDataStore']?.value || '');
      const outputDirectory = String(tp['outputDirectory']?.value || '');
      const fileDirectory = resolveOutputFileDirectory(folderGuid, outputDirectory, folderDataStores);
      const fileExtension = normalizeFileExtension(String(tp['outputFileExtension']?.value || defaultFileExtensionForOutput(output)));
      const rolloverMethod = normalizeRolloverMethod(String(tp['outputFileSizeLimitMethod']?.value || 'By Size'));

      props['write-file.fileDirectory'] = fileDirectory;
      props['write-file.fileNamePrefix'] = String(tp['outputBaseFileName']?.value || output.label || output.name);
      props['write-file.fileExtension'] = fileExtension;
      props['write-file.rolloverMethod'] = rolloverMethod;
      props['write-file.rolloverSize'] = Number(tp['outputFileSizeLimitValue']?.value ?? 1024);
      props['write-file.rolloverFrequency'] = String(tp['outputFileFrequency']?.value || 'Hourly');

      if (output.connector === 'esri-out-csv-file-log') {
        props['delimited.fieldDelimiter'] = String(adapterProps['AttributeSeparator']?.value || ',');
        props['delimited.messageDelimiter'] = String(adapterProps['MessageSeparator']?.value || '\n');
      } else if (output.connector === 'esri-out-json-file-log') {
        props['write-file.jsonLines'] = false;
        props['write-file.fileHeader'] = String(tp['outputFileHeader']?.value || '');
        props['write-file.fileFooter'] = String(tp['outputFileFooter']?.value || '');
      }
    }
  }
  
  return props;
}

function defaultFileExtensionForOutput(output: Output): string {
  if (output.connector === 'esri-out-csv-file-log') return '.csv';
  if (output.connector === 'esri-out-geojson-file-log') return '.geojson';
  if (output.connector === 'esri-out-json-file-log') return '.json';
  return '.json';
}

function getWriteFileFormatName(output: Output): string | undefined {
  if (output.connector === 'esri-out-csv-file-log') return 'delimited';
  if (output.connector === 'esri-out-geojson-file-log') return 'geo-json';
  if (output.connector === 'esri-out-json-file-log') return 'json';
  return undefined;
}

function normalizeFileExtension(extension: string): string {
  const trimmed = extension.trim();
  if (!trimmed) return '.json';
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

function normalizeRolloverMethod(method: string): string {
  return method.toLowerCase().includes('time') ? 'byTime' : 'bySize';
}

function resolveOutputFileDirectory(
  folderGuid: string,
  outputDirectory: string,
  folderDataStores: FolderDataStore[]
): string {
  const baseDirectory = folderGuid ? resolveFolderPath(folderGuid, folderDataStores) : '';
  if (!outputDirectory) return baseDirectory;
  if (/^(?:[a-zA-Z]:[\\/]|[\\/]{2}|[\\/])/.test(outputDirectory)) return outputDirectory;
  if (!baseDirectory) return outputDirectory;
  const separator = baseDirectory.includes('\\') ? '\\' : '/';
  return `${baseDirectory.replace(/[\\/]+$/, '')}${separator}${outputDirectory.replace(/^[\\/]+/, '')}`;
}

/**
 * Resolves the GeoEvent definition for a service by looking up the first input's
 * adapter properties to find the definition name, then matching against definitions.
 */
function resolveGeoDefForService(
  service: GeoEventService,
  config: GeoEventConfiguration
): GeoEventDefinition | undefined {
  for (const svcInput of service.inputs) {
    const globalInput = config.inputs.find(i => i.name === svcInput.inputRef);
    if (!globalInput) continue;
    const ap = globalInput.adapter?.properties;
    const defCandidates = [
      ap?.['ExistingFixedGeoEventDefinitionName']?.value,
      ap?.['ExistingGeoEventDefinitionName']?.value,
      ap?.['NewFixedGeoEventDefinitionName']?.value,
      ap?.['NewGeoEventDefinitionName']?.value,
    ].filter((v): v is string => typeof v === 'string' && v.length > 0);
    for (const candidate of defCandidates) {
      const geoDef = config.geoEventDefinitions.find(d => d.name === candidate);
      if (geoDef) return geoDef;
    }
  }
  return undefined;
}

/**
 * Builds field-mapper tool properties for in-place migration.
 * Converts GeoEvent Integer fields (mapped as Int64) to Int32 using Arcade Number()
 * to match existing feature layer schemas that use esriFieldTypeInteger (Int32).
 * All other fields pass through with their original types.
 */
function buildInPlaceFieldMapperProperties(
  geoDef: GeoEventDefinition,
  ui: { left: number; top: number }
): Record<string, unknown> {
  const mappings = geoDef.fieldDefinitions
    .filter(f => f.type !== 'Geometry')
    .map(field => {
      const velocityType = mapFieldType(field.type);
      const tags = (field.tags || []).map(t => mapTagName(t.name));
      // GeoEvent Integer and Long map to Int64, but existing feature layers use Int32.
      // Skip objectid — it's a system OID field that should remain Int64.
      const isIntegerType = field.type === 'Integer' || field.type === 'Long';
      const needsConversion = isIntegerType && field.name.toLowerCase() !== 'objectid';
      return {
        nullable: true,
        name: field.name,
        tags,
        expression: needsConversion
          ? `Number($feature["${field.name}"])`
          : `$feature["${field.name}"]`,
        dataType: needsConversion ? 'Int32' : velocityType,
      };
    });

  return {
    'field-mapper.schemaFieldMappings': { mappings },
    'field-mapper.featuresPerTrack': 6,
    ui: { width: 240, height: 70, left: ui.left, top: ui.top },
    arcadeSampleValues: {},
  };
}

/**
 * Builds arcadeSampleValues from a GeoEvent definition's field schema.
 * Velocity uses these to populate the Arcade expression builder's $feature tab
 * with sample values so authors can test expressions before running the analytic.
 *
 * Matches the structure observed in real Velocity configs:
 *   { "$feature": { "attributes": { "fieldName": <sampleValue>, ... } } }
 *
 * When no definition is available, returns the standard empty object.
 */
function buildArcadeSampleValues(geoDef: GeoEventDefinition | undefined): Record<string, unknown> {
  if (!geoDef || !geoDef.fieldDefinitions || geoDef.fieldDefinitions.length === 0) {
    return {};
  }

  const attributes: Record<string, unknown> = {};
  for (const field of geoDef.fieldDefinitions) {
    if (field.type === 'Geometry') continue;

    // Provide a representative sample value based on the GeoEvent field type
    switch (field.type) {
      case 'Integer':
      case 'Short':
        attributes[field.name] = 0;
        break;
      case 'Long':
        attributes[field.name] = 0;
        break;
      case 'Float':
      case 'Double':
        attributes[field.name] = 1.1;
        break;
      case 'Boolean':
        attributes[field.name] = true;
        break;
      case 'Date':
        attributes[field.name] = '2020-01-01T00:00:00.000Z';
        break;
      case 'String':
      default:
        attributes[field.name] = 'Pacific';
        break;
    }
  }

  return { '$feature': { attributes } };
}

/**
 * Set of Velocity tool IDs that accept a join (secondary) input port.
 * These tools use UnionToolDefinition or OptionalJoinUnionToolDefinition
 * and require "target" and "join" named ports instead of default port-in-{id}.
 */
const MULTI_INPUT_TOOLS = new Set([
  'join-features',
  'filter-by-geometry',
  'merge',
  'multi-merge',
  'overlay',
  'distance-to-feature-calculator',
  'network-snapper',
  'find-similar-locations',
  'incident-detector',      // OptionalJoin
  'aggregate-points',       // OptionalJoin
  'summarize-within',       // OptionalJoin
  'generalized-linear-regression',  // OptionalJoin
  'forest-based-regression',        // OptionalJoin
]);

/**
 * Resolves the Velocity tool name from a GeoEvent processor URI
 * using the functional matrix, falling back to a URI-based heuristic.
 */
function resolveToolName(processorUri: string, matrix: LoadedFunctionalMatrix): string {
  const entry = lookupProcessorMapping(matrix, processorUri);
  if (entry?.velocityEquivalent) {
    return entry.velocityEquivalent;
  }

  // Fallback: extract from URI pattern "com.esri.ges.processor/Name"
  const parts = processorUri.split('/');
  const rawName = parts[parts.length - 1] || '';
  // Convert PascalCase to kebab-case: e.g. "BufferCreator" → "buffer-creator"
  return rawName
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

/**
 * Gets tool properties from GeoEvent processor, mapped to actual
 * Velocity property keys verified from source code.
 */
/**
 * Maps GeoEvent data types to Velocity targetType values for field-calculator.
 */
/**
 * Starter ArcGIS Arcade expressions for GeoEvent geometry creators that map to
 * Velocity's Calculate Field. Keyed by the Arcade function name carried in the
 * matrix `arcadeAlternative.functions`. Unary functions produce a directly
 * usable call; binary/array functions emit a TODO marker for the operand that
 * a single-feature Calculate Field cannot supply on its own.
 */
const ARCADE_GEOMETRY_STARTERS: Record<string, string> = {
  ConvexHull: 'ConvexHull(Geometry($feature))',
  Extent: 'Extent(Geometry($feature))',
  Simplify: 'Simplify(Geometry($feature))',
  Generalize: 'Generalize(Geometry($feature), 10)',
  Difference: 'Difference(Geometry($feature), /* TODO: supply the second geometry */ Geometry($feature))',
  Intersection: 'Intersection(Geometry($feature), /* TODO: supply the second geometry */ Geometry($feature))',
  SymmetricDifference: 'SymmetricDifference(Geometry($feature), /* TODO: supply the second geometry */ Geometry($feature))',
  Union: 'Union([Geometry($feature) /*, TODO: add geometries */])',
};

/**
 * Returns a starter Arcade expression for the first recognized geometry
 * function in the mapping's arcadeAlternative, or undefined when none applies.
 */
function buildArcadeGeometryStarter(functions: string[] | undefined): string | undefined {
  if (!functions) return undefined;
  for (const fn of functions) {
    const starter = ARCADE_GEOMETRY_STARTERS[fn];
    if (starter) return starter;
  }
  return undefined;
}

function mapGeoEventTypeToVelocity(geType: string): string {
  switch (geType.toLowerCase()) {
    case 'string': return 'String';
    case 'boolean': return 'String';
    case 'integer':
    case 'short':
    case 'long': return 'Int64';
    case 'float': return 'Float32';
    case 'double': return 'Float64';
    case 'date': return 'Date';
    default: return 'String';
  }
}

function getProcessorPropertyString(
  processorProps: Record<string, { value: unknown; type: string }>,
  names: string[],
): string {
  for (const name of names) {
    const value = processorProps[name]?.value;
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return '';
}

function splitProcessorFieldList(value: unknown): string[] {
  return String(value || '')
    .split(',')
    .map(field => field.trim())
    .filter(field => field.length > 0);
}

function isGeometryFieldReference(fieldName: string): boolean {
  return fieldName.toUpperCase() === 'GEOMETRY';
}

function findGeoEventDefinitionByProcessorProperty(
  processorProps: Record<string, { value: unknown; type: string }>,
  geoEventDefinitions: GeoEventDefinition[] | undefined,
  propertyNames: string[],
): GeoEventDefinition | undefined {
  const name = getProcessorPropertyString(processorProps, propertyNames);
  if (!name || !geoEventDefinitions) return undefined;
  return geoEventDefinitions.find(def => def.name === name);
}

function buildFieldMapperMappings(
  processorProps: Record<string, { value: unknown; type: string }>,
  sourceGeoDef: GeoEventDefinition | undefined,
  geoEventDefinitions: GeoEventDefinition[] | undefined,
  flattenNestedSchema = true,
): Array<Record<string, unknown>> {
  const inputGeoDef = findGeoEventDefinitionByProcessorProperty(
    processorProps,
    geoEventDefinitions,
    ['inputGeoEventDefinition', 'inputGeoEventDefinitionName'],
  );
  const outputGeoDef = findGeoEventDefinitionByProcessorProperty(
    processorProps,
    geoEventDefinitions,
    ['outputGeoEventDefinition', 'outputGeoEventDefinitionName'],
  );
  const sourceFields = inputGeoDef?.fieldDefinitions ?? sourceGeoDef?.fieldDefinitions ?? [];
  const knownFields = sourceFields.map(field => field.name);
  const doFlatten = flattenNestedSchema && hasNestedOrArrayFields(inputGeoDef ?? sourceGeoDef);
  const inputFields = splitProcessorFieldList(processorProps['inputFields']?.value);
  const outputFields = splitProcessorFieldList(processorProps['outputFields']?.value);
  const targetFields = outputFields.length > 0
    ? outputFields
    : outputGeoDef?.fieldDefinitions.map(field => field.name) ?? [];

  const mappings: Array<Record<string, unknown>> = [];
  for (const [index, outputFieldName] of targetFields.entries()) {
    if (isGeometryFieldReference(outputFieldName)) continue;
    const targetField = outputGeoDef?.fieldDefinitions.find(field => field.name === outputFieldName)
      ?? sourceFields.find(field => field.name === outputFieldName);
    if (targetField?.type === 'Geometry') continue;

    // GeoEvent wraps Field Mapper input tokens in double quotes; strip that
    // wrapping before resolving the token so plain renames and structural
    // accessor paths are recognized instead of falling through to a manual
    // "ACTION REQUIRED" placeholder.
    const inputExpression = stripFieldQuotes(inputFields[index]?.trim() || outputFieldName);
    const sourceField = sourceFields.find(field => field.name === inputExpression);
    const expression = buildFieldMapperExpression(inputExpression, sourceField, knownFields, doFlatten);
    const dataType = targetField
      ? mapFieldType(targetField.type)
      : sourceField
        ? mapFieldType(sourceField.type)
        : 'String';

    mappings.push({
      nullable: true,
      name: outputFieldName,
      tags: (targetField?.tags || []).map(tag => mapTagName(tag.name)),
      expression,
      dataType,
    });
  }

  if (mappings.length === 0 && sourceFields.length > 0) {
    for (const field of sourceFields) {
      if (field.type === 'Geometry') continue;
      mappings.push({
        nullable: true,
        name: field.name,
        tags: (field.tags || []).map(tag => mapTagName(tag.name)),
        expression: `$feature["${field.name}"]`,
        dataType: mapFieldType(field.type),
      });
    }
  }

  return mappings;
}

/**
 * Resolves the Arcade expression for a single Field Mapper input token.
 *  - An exact source field name maps to a direct `$feature["name"]` reference.
 *  - A structural accessor path (nested object / array element) maps to the
 *    Velocity flattened field name when flattening is enabled, or to nested/
 *    array Arcade access that mirrors GeoEvent when flattening is disabled.
 *  - Anything else (operators, function calls) falls back to general GeoEvent
 *    expression conversion.
 */
function buildFieldMapperExpression(
  inputExpression: string,
  sourceField: FieldDefinition | undefined,
  knownFields: string[],
  doFlatten: boolean,
): string {
  if (sourceField) {
    return `$feature["${sourceField.name}"]`;
  }
  const tokenized = tokenizeFieldPath(inputExpression);
  if (tokenized && tokenized.keys.length > 0) {
    return doFlatten
      ? `$feature[${JSON.stringify(geoEventPathToFlattenedField(inputExpression))}]`
      : structuralPathToNestedArcade(tokenized);
  }
  if (tokenized) {
    // Bare identifier that is not a known source field — reference it directly.
    return `$feature[${JSON.stringify(tokenized.root)}]`;
  }
  return stripAutoConvertedArcadeComment(convertGeoEventExpressionToArcade(inputExpression, knownFields));
}


function getToolProperties(
  toolName: string,
  processorProps: Record<string, { value: unknown; type: string }>,
  ui: { left: number; top: number },
  geoDef?: GeoEventDefinition,
  geoEventDefinitions?: GeoEventDefinition[],
  arcadeAlternative?: { functions: string[]; note: string },
  flattenNestedSchema = true,
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    ui: {
      width: 240,
      height: 70,
      left: ui.left,
      top: ui.top,
    },
    arcadeSampleValues: buildArcadeSampleValues(geoDef),
  };
  
  switch (toolName) {
    case 'buffer-creator': {
      const distance = processorProps['bufferDistance']?.value || '100';
      const unit = processorProps['unit']?.value || 'Meter';
      const unitLower = String(unit).toLowerCase() + 's';
      props[`${toolName}.bufferBy`] = 'Distance';
      props[`${toolName}.expression`] = '';
      props[`${toolName}.fieldName`] = '';
      props[`${toolName}.distance`] = `${distance} ${unitLower}`;
      props[`${toolName}.appendBufferDistance`] = false;
      props[`${toolName}.method`] = processorProps['bufferGeodesic']?.value === 'true' ? 'Geodesic' : 'Planar';
      break;
    }
    case 'field-calculator': {
      // GeoEvent Field Calculator (Regular Expression) extracts or replaces text
      // via a regex pattern. Velocity and Arcade have no native regular-expression
      // support, so emit an honest note with real alternatives rather than a
      // placeholder or a misleading "rewrite as Arcade" instruction.
      const regexExpr = String(processorProps['regexExpression']?.value || '');
      if (regexExpr) {
        const regexFieldName = String(
          processorProps['existingFieldName']?.value ||
          processorProps['newFieldName']?.value ||
          processorProps['targetField']?.value ||
          '',
        );
        props[`${toolName}.fieldCalculations`] = [{
          expression:
            `/* ACTION REQUIRED: GeoEvent Field Calculator (Regular Expression) applied the pattern ` +
            `${sanitizeArcadeCommentValue(JSON.stringify(regexExpr))} to extract or replace text. ` +
            `Velocity and Arcade have no native regular-expression support, so this cannot be reproduced directly. ` +
            `Options: apply the regex upstream before ingest, route through an external service, or approximate ` +
            `with Arcade text functions (Find/Mid/Left/Right/Replace/Split) for simple, fixed patterns. */ null`,
          fieldName: regexFieldName,
          targetType: 'String',
          fieldType: regexFieldName ? 'existingField' : 'newField',
        }];
        props[`${toolName}.featuresPerTrack`] = 6;
        break;
      }
      // Arcade expressions replace GeoEvent expressions — mark for manual review.
      // Geometry creators (e.g. Convex Hull, Union) have no GeoEvent expression;
      // seed a starter Arcade call from the matrix arcadeAlternative metadata.
      const expr = String(processorProps['expression']?.value || '');
      const knownFields = (geoDef?.fieldDefinitions ?? []).map((f) => f.name);
      const starterExpr = expr ? undefined : buildArcadeGeometryStarter(arcadeAlternative?.functions);
      const arcadeExpr = starterExpr ?? convertGeoEventExpressionToArcade(expr, knownFields);
      const resultDest = String(processorProps['resultDestination']?.value || 'Existing Field');
      const fieldType = resultDest === 'New Field' ? 'newField' : 'existingField';
      const fieldName = fieldType === 'existingField'
        ? String(processorProps['existingFieldName']?.value || '')
        : String(processorProps['newFieldName']?.value || '');
      const geType = String(processorProps['newFieldType']?.value || 'String');
      const targetType = starterExpr ? 'Geometry' : mapGeoEventTypeToVelocity(geType);
      props[`${toolName}.fieldCalculations`] = [{
        expression: arcadeExpr,
        fieldName,
        targetType,
        fieldType,
      }];
      props[`${toolName}.featuresPerTrack`] = 6;
      break;
    }
    case 'field-mapper': {
      props[`${toolName}.schemaFieldMappings`] = {
        mappings: buildFieldMapperMappings(processorProps, geoDef, geoEventDefinitions, flattenNestedSchema),
      };
      props[`${toolName}.featuresPerTrack`] = 6;
      break;
    }
    case 'field-selector': {
      const fields = String(processorProps['targetFields']?.value || processorProps['fields']?.value || '');
      const selectedFields = splitProcessorFieldList(fields)
        .filter(fieldName => !isGeometryFieldReference(fieldName))
        .join(',');
      props[`${toolName}.fields`] = selectedFields || fields;
      props[`${toolName}.dropGeometry`] = false;
      break;
    }
    case 'filter-by-expression': {
      // GeoEvent filter conditions need manual conversion to Arcade
      props[`${toolName}.expression`] = '/* ACTION REQUIRED: Convert GeoEvent filter condition to Arcade expression.\n' +
        '   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/\n' +
        '   Velocity Arcade guide: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm */ null';
      break;
    }
    case 'filter-by-geometry': {
      const spatialOp = String(processorProps['spatialOperator']?.value || 'Intersects');
      props[`${toolName}.spatialRelationship`] = spatialOp;
      props[`${toolName}.spatialNearDistance`] = '';
      props[`${toolName}.joinTimeWindow`] = '60 seconds';
      props[`${toolName}.timeWindow`] = '4 hours';
      // G-13: Geofence enter/exit flags for dynamic geofencing behavior
      const firstAsEnter = processorProps['firstEventAsEnter']?.value;
      const firstAsExit = processorProps['firstEventAsExit']?.value;
      props[`${toolName}.firstEventAsEnter`] = firstAsEnter === 'true' || firstAsEnter === true;
      props[`${toolName}.firstEventAsExit`] = firstAsExit === 'true' || firstAsExit === true;
      break;
    }
    case 'incident-detector': {
      const openExpr = String(processorProps['openingCondition']?.value || '');
      const closeExpr = String(processorProps['closingCondition']?.value || '');
      const idKnownFields = (geoDef?.fieldDefinitions ?? []).map((f) => f.name);
      props[`${toolName}.openCondition`] = true;
      props[`${toolName}.ocExpression`] = convertGeoEventExpressionToArcade(openExpr, idKnownFields);
      props[`${toolName}.ocSpatialRelationship`] = 'None';
      props[`${toolName}.ocSpatialNearDistance`] = '';
      props[`${toolName}.ocFirstEventAsEnter`] = false;
      props[`${toolName}.ocFirstEventAsExit`] = false;
      props[`${toolName}.ocMatchAll`] = true;
      props[`${toolName}.ocTime`] = '';
      props[`${toolName}.closeCondition`] = true;
      props[`${toolName}.ccExpression`] = convertGeoEventExpressionToArcade(closeExpr, idKnownFields);
      props[`${toolName}.ccSpatialRelationship`] = 'None';
      props[`${toolName}.ccSpatialNearDistance`] = '';
      props[`${toolName}.ccMatchAll`] = true;
      props[`${toolName}.ccTime`] = '';
      props[`${toolName}.returnIncidentsOnly`] = processorProps['incidentType']?.value === 'PointInTime' ? false : true;
      props[`${toolName}.trackIdFields`] = '$TRACK_ID';
      const expiryTime = processorProps['expiryTime']?.value;
      props[`${toolName}.timeWindow`] = expiryTime ? `${expiryTime} seconds` : '3600 seconds';
      props[`${toolName}.closeOnExpiration`] = true;
      // G-12: Join properties for incident detection with reference data
      const idJoinFields = String(processorProps['joinFields']?.value || processorProps['enrichmentFields']?.value || '');
      if (idJoinFields) {
        props[`${toolName}.joinFields`] = idJoinFields;
      }
      const idJoinTimeWindow = String(processorProps['joinTimeWindow']?.value || '');
      props[`${toolName}.joinTimeWindow`] = idJoinTimeWindow || '60 seconds';
      break;
    }
    case 'join-features': {
      // Used for GeoTagger, EventJoiner, FieldEnricher mappings
      props[`${toolName}.joinOperation`] = 'OneToOne';
      props[`${toolName}.spatialRelationship`] = String(processorProps['spatialOperator']?.value || 'Intersects');
      props[`${toolName}.spatialNearDistance`] = '';
      props[`${toolName}.temporalRelationship`] = 'None';
      props[`${toolName}.temporalNearDistance`] = '';
      props[`${toolName}.attributeRelationship`] = '';
      props[`${toolName}.joinCondition`] = '';
      props[`${toolName}.statistics`] = '';
      props[`${toolName}.retainAllFeatures`] = 'false';
      props[`${toolName}.replaceGeometry`] = false;
      props[`${toolName}.joinTimeWindow`] = '60 seconds';
      props[`${toolName}.timeWindow`] = '4 hours';
      props[`${toolName}.firstEventAsEnter`] = false;
      props[`${toolName}.firstEventAsExit`] = false;
      break;
    }
    case 'project': {
      const wkid = parseInt(String(processorProps['outwkid']?.value || '4326'), 10);
      props[`${toolName}.outSr`] = wkid;
      break;
    }
    case 'event-volume-controller': {
      const interval = String(processorProps['interval']?.value || '60');
      props[`${toolName}.trackIdFields`] = String(processorProps['trackIdFields']?.value || '$TRACK_ID');
      props[`${toolName}.maxEventsPerInterval`] = 1;
      props[`${toolName}.interval`] = `${interval} seconds`;
      break;
    }
    case 'detect-gaps': {
      const gapDuration = String(processorProps['gapDuration']?.value || '300');
      props[`${toolName}.trackIdFields`] = '$TRACK_ID';
      props[`${toolName}.gapDetectionInterval`] = '30 seconds';
      props[`${toolName}.gapDuration`] = `${gapDuration} seconds`;
      props[`${toolName}.timeWindow`] = '7 days';
      props[`${toolName}.notificationMode`] = 'Continuous';
      break;
    }
    case 'motion-calculator': {
      props[`${toolName}.trackIdFields`] = '$TRACK_ID';
      props[`${toolName}.historyDepth`] = 3;
      props[`${toolName}.method`] = 'Geodesic';
      props[`${toolName}.distanceTolerance`] = '0 meters';
      props[`${toolName}.timeSpanTolerance`] = '0 seconds';
      props[`${toolName}.timeWindow`] = '60 minutes';
      break;
    }
    case 'reconstruct-tracks': {
      props[`${toolName}.trackIdFields`] = '$TRACK_ID';
      props[`${toolName}.method`] = 'Geodesic';
      props[`${toolName}.splitDistance`] = '';
      props[`${toolName}.splitDuration`] = '';
      props[`${toolName}.bufferExpression`] = '';
      props[`${toolName}.statistics`] = '';
      break;
    }
    case 'overlay': {
      // Map specific GeoEvent geometry operations to Velocity overlay operations
      props[`${toolName}.operation`] = 'Intersect';
      break;
    }
    case 'range-fan': {
      const distance = String(processorProps['range']?.value || processorProps['distance']?.value || '1000');
      props[`${toolName}.bearing`] = 0;
      props[`${toolName}.bearingBy`] = 'Bearing Value';
      props[`${toolName}.bearingExpression`] = '';
      props[`${toolName}.bearingFieldName`] = '';
      props[`${toolName}.arcAngle`] = 45;
      props[`${toolName}.arcAngleBy`] = 'Arc Angle';
      props[`${toolName}.arcAngleExpression`] = '';
      props[`${toolName}.arcAngleFieldName`] = '';
      props[`${toolName}.rangeDistance`] = `${distance} meters`;
      props[`${toolName}.rangeProvidedBy`] = 'Range Distance';
      props[`${toolName}.rangeDistanceExpression`] = '';
      props[`${toolName}.rangeFieldName`] = '';
      break;
    }
    case 'merge': {
      // No user-configurable properties — schema-driven merging
      break;
    }
    case 'multi-merge': {
      // No user-configurable properties — schema-driven merging of N inputs
      break;
    }
    case 'detect-idle': {
      const idleDuration = String(processorProps['idleDuration']?.value || processorProps['idleTimeout']?.value || '300');
      props[`${toolName}.trackIdFields`] = '$TRACK_ID';
      props[`${toolName}.idleDuration`] = `${idleDuration} seconds`;
      props[`${toolName}.timeWindow`] = '7 days';
      props[`${toolName}.notificationMode`] = String(processorProps['notificationMode']?.value || 'OnChange');
      break;
    }
    case 'route-by-expressions': {
      // Routes events to different output ports based on Arcade expressions.
      // Velocity expects a `routingExpressions` object whose `expressions`
      // array carries one entry per route, each with a stable route `id`,
      // 0-based `order`, and a `destinationId` linking it to a downstream
      // component. Choice nodes overwrite this with per-clause routes (and a
      // native Otherwise) and back-fill destinationId during pipeline wiring;
      // this fallback covers the rare non-choice source.
      const routeExprStr = String(processorProps['routeExpressions']?.value || processorProps['expression']?.value || '');
      const rbeKnownFields = (geoDef?.fieldDefinitions ?? []).map((f) => f.name);
      const arcadeExpr = routeExprStr
        ? convertGeoEventExpressionToArcade(routeExprStr, rbeKnownFields)
        : 'true';
      props[`${toolName}.routingExpressions`] = {
        expressions: [
          {
            label: 'Route 1',
            id: uuidv4(),
            expression: arcadeExpr,
            order: 0,
            destinationId: '',
          },
        ],
      };
      break;
    }
    case 'add-xyz-values': {
      props[`${toolName}.x`] = String(processorProps['xField']?.value || 'x');
      props[`${toolName}.y`] = String(processorProps['yField']?.value || 'y');
      const zField = processorProps['zField']?.value;
      if (zField) {
        props[`${toolName}.z`] = String(zField);
      }
      break;
    }
    case 'feature-to-point': {
      const replaceGeom = processorProps['replaceGeometry']?.value;
      props[`${toolName}.inside`] = replaceGeom === 'true';
      break;
    }
    case 'filter-by-time-range': {
      const timeRange = String(processorProps['timeRange']?.value || '');
      // Velocity uses comma-separated epoch millis: "startEpoch,endEpoch" or "null" for open-ended
      props[`${toolName}.time`] = timeRange || '1719943200000,null';
      break;
    }
    case 'delay-and-reorder-tracks': {
      const timeWindow = String(processorProps['timeWindow']?.value || '60');
      const maxFeatures = String(processorProps['maxFeatures']?.value || '256');
      props[`${toolName}.timeWindow`] = `${timeWindow} seconds`;
      props[`${toolName}.maxFeaturesWithinTimeWindow`] = parseInt(maxFeatures, 10) || 256;
      break;
    }
    case 'distance-to-feature-calculator': {
      props[`${toolName}.spatialRelationship`] = String(processorProps['spatialRelationship']?.value || 'NearGeodesic');
      props[`${toolName}.outputDistanceUnits`] = 9001; // WKID code: 9001 = meters
      props[`${toolName}.keepOutsideDistanceFeatures`] = true;
      props[`${toolName}.spatialSearchRadius`] = '100 miles';
      props[`${toolName}.attributeRelationship`] = '';
      props[`${toolName}.joinFields`] = '*';
      props[`${toolName}.joinTimeWindow`] = '60 seconds';
      break;
    }
    // --- BAT-only tools (Phase 2 step 13) ---
    case 'aggregate-points': {
      props[`${toolName}.binType`] = String(processorProps['binType']?.value || 'Square');
      props[`${toolName}.binSize`] = String(processorProps['binSize']?.value || '1 kilometers');
      props[`${toolName}.statistics`] = processorProps['statistics']?.value || '';
      props[`${toolName}.timeStepInterval`] = String(processorProps['timeStepInterval']?.value || '');
      props[`${toolName}.timeStepRepeat`] = String(processorProps['timeStepRepeat']?.value || '');
      props[`${toolName}.timeStepAlignment`] = String(processorProps['timeStepAlignment']?.value || 'EndTime');
      props[`${toolName}.timeStepReference`] = String(processorProps['timeStepReference']?.value || '');
      break;
    }
    case 'density-calculator': {
      props[`${toolName}.binType`] = String(processorProps['binType']?.value || 'Square');
      props[`${toolName}.cellSize`] = String(processorProps['cellSize']?.value || '1000 meters');
      props[`${toolName}.radius`] = String(processorProps['radius']?.value || '10000 meters');
      props[`${toolName}.outputUnit`] = String(processorProps['outputUnit']?.value || 'SquareKilometers');
      props[`${toolName}.fields`] = String(processorProps['fields']?.value || '');
      props[`${toolName}.weight`] = String(processorProps['weight']?.value || 'Uniform');
      props[`${toolName}.timeStepInterval`] = String(processorProps['timeStepInterval']?.value || '');
      props[`${toolName}.timeStepRepeat`] = String(processorProps['timeStepRepeat']?.value || '');
      props[`${toolName}.timeStepAlignment`] = String(processorProps['timeStepAlignment']?.value || 'EndTime');
      props[`${toolName}.timeStepReference`] = String(processorProps['timeStepReference']?.value || '');
      break;
    }
    case 'dissolve': {
      props[`${toolName}.dissolveFields`] = String(processorProps['dissolveFields']?.value || '');
      props[`${toolName}.multiPart`] = processorProps['multiPart']?.value === 'true';
      props[`${toolName}.statistics`] = processorProps['statistics']?.value || '';
      break;
    }
    case 'find-dwell-locations': {
      props[`${toolName}.trackIdFields`] = String(processorProps['trackIdFields']?.value || '$TRACK_ID');
      props[`${toolName}.distanceTolerance`] = String(processorProps['distanceTolerance']?.value || '100 meters');
      props[`${toolName}.timeTolerance`] = String(processorProps['timeTolerance']?.value || '5 minutes');
      props[`${toolName}.method`] = String(processorProps['method']?.value || 'Planar');
      props[`${toolName}.outputType`] = String(processorProps['outputType']?.value || 'DwellFeatures');
      props[`${toolName}.summaryStatistics`] = processorProps['summaryStatistics']?.value || '';
      props[`${toolName}.dwellDetectionAlgorithm`] = String(processorProps['dwellDetectionAlgorithm']?.value || 'TimeAndDistance');
      break;
    }
    case 'find-hot-spots': {
      props[`${toolName}.binType`] = String(processorProps['binType']?.value || 'Square');
      props[`${toolName}.binSize`] = String(processorProps['binSize']?.value || '1 kilometers');
      props[`${toolName}.neighborhoodSize`] = String(processorProps['neighborhoodSize']?.value || '3');
      props[`${toolName}.timeStepInterval`] = String(processorProps['timeStepInterval']?.value || '');
      props[`${toolName}.timeStepAlignment`] = String(processorProps['timeStepAlignment']?.value || 'EndTime');
      props[`${toolName}.timeStepReference`] = String(processorProps['timeStepReference']?.value || '');
      break;
    }
    case 'find-point-clusters': {
      props[`${toolName}.clusteringMethod`] = String(processorProps['clusteringMethod']?.value || 'DBSCAN');
      props[`${toolName}.minPoints`] = parseInt(String(processorProps['minPoints']?.value || '3'), 10);
      props[`${toolName}.searchDistance`] = String(processorProps['searchDistance']?.value || '100 meters');
      props[`${toolName}.useTime`] = processorProps['useTime']?.value === 'true';
      props[`${toolName}.searchDuration`] = String(processorProps['searchDuration']?.value || '');
      break;
    }
    case 'find-similar-locations': {
      props[`${toolName}.matchMethod`] = String(processorProps['matchMethod']?.value || 'AttributeValues');
      props[`${toolName}.attributesOfInterest`] = String(processorProps['attributesOfInterest']?.value || '');
      props[`${toolName}.mostOrLeastSimilar`] = String(processorProps['mostOrLeastSimilar']?.value || 'MostSimilar');
      props[`${toolName}.numberOfResults`] = parseInt(String(processorProps['numberOfResults']?.value || '10'), 10);
      props[`${toolName}.attributesToAppend`] = String(processorProps['attributesToAppend']?.value || '');
      break;
    }
    case 'forest-based-regression': {
      props[`${toolName}.variableToPredict`] = String(processorProps['variableToPredict']?.value || '');
      props[`${toolName}.explanatoryVariables`] = String(processorProps['explanatoryVariables']?.value || '');
      props[`${toolName}.numberOfTrees`] = parseInt(String(processorProps['numberOfTrees']?.value || '100'), 10);
      props[`${toolName}.minimumLeafSize`] = parseInt(String(processorProps['minimumLeafSize']?.value || '5'), 10);
      props[`${toolName}.maximumTreeDepth`] = parseInt(String(processorProps['maximumTreeDepth']?.value || ''), 10) || undefined;
      props[`${toolName}.sampleSize`] = parseInt(String(processorProps['sampleSize']?.value || '100'), 10);
      props[`${toolName}.percentageForValidation`] = parseInt(String(processorProps['percentageForValidation']?.value || '10'), 10);
      props[`${toolName}.variableToPredictIsCategorical`] = processorProps['variableToPredictIsCategorical']?.value === 'true';
      break;
    }
    case 'generalized-linear-regression': {
      props[`${toolName}.dependentVariable`] = String(processorProps['dependentVariable']?.value || '');
      props[`${toolName}.explanatoryVariables`] = String(processorProps['explanatoryVariables']?.value || '');
      props[`${toolName}.modelType`] = String(processorProps['modelType']?.value || 'Continuous');
      break;
    }
    case 'journey-calculator': {
      props[`${toolName}.trackIdFields`] = String(processorProps['trackIdFields']?.value || '$TRACK_ID');
      props[`${toolName}.method`] = String(processorProps['method']?.value || 'Geodesic');
      props[`${toolName}.output`] = String(processorProps['output']?.value || 'All');
      props[`${toolName}.dwellDistance`] = String(processorProps['dwellDistance']?.value || '100 meters');
      props[`${toolName}.dwellDuration`] = String(processorProps['dwellDuration']?.value || '5 minutes');
      props[`${toolName}.gapDuration`] = String(processorProps['gapDuration']?.value || '1 hours');
      props[`${toolName}.includeGaps`] = processorProps['includeGaps']?.value === 'true';
      props[`${toolName}.statistics`] = processorProps['statistics']?.value || '';
      break;
    }
    case 'network-snapper': {
      props[`${toolName}.trackIdFields`] = String(processorProps['trackIdFields']?.value || '$TRACK_ID');
      props[`${toolName}.searchDistance`] = String(processorProps['searchDistance']?.value || '100 meters');
      props[`${toolName}.joinFields`] = String(processorProps['joinFields']?.value || '*');
      props[`${toolName}.retainOnlySnappedFeatures`] = processorProps['retainOnlySnappedFeatures']?.value !== 'false';
      break;
    }
    case 'summarize-attributes': {
      props[`${toolName}.groupFields`] = String(processorProps['groupFields']?.value || '');
      props[`${toolName}.statistics`] = processorProps['statistics']?.value || '';
      break;
    }
    case 'summarize-within': {
      props[`${toolName}.binType`] = String(processorProps['binType']?.value || 'Square');
      props[`${toolName}.binSize`] = String(processorProps['binSize']?.value || '1 kilometers');
      props[`${toolName}.statistics`] = processorProps['statistics']?.value || '';
      props[`${toolName}.summarizeShapes`] = processorProps['summarizeShapes']?.value === 'true';
      break;
    }
  }
  
  return props;
}

/**
 * Converts GeoEvent service to Velocity realtime analytic.
 * Uses functional matrix for tool name resolution and generates
 * proper pipeline connections with correct port naming:
 * - Feeds/Sources: from port = "" (implicit default output)
 * - Single-input tools: to port = "port-in-{toolId}"
 * - Multi-input tools: to port = "target" (primary) or "join" (secondary)
 * - Outputs: to port = "port-in-{outputId}"
 */
function serviceToRealtimeAnalytic(
  service: GeoEventService,
  config: GeoEventConfiguration,
  idMap: Map<string, string>,
  _mappingResult: ConfigurationMappingResult,
  matrix: LoadedFunctionalMatrix,
  targetPlatform: TargetPlatform = 'velocity-online',
  migrationStrategy: MigrationStrategy = 'side-by-side',
  warnings: string[] = [],
  flattenNestedSchema = true,
): VelocityRealtimeAnalytic {
  const analyticId = generateVelocityId();
  
  // Build feeds array (references to input feeds)
  const feeds: AnalyticFeedRef[] = [];
  let currentLeft = -250;
  const topPosition = 70;

  // GeoEvent UI Designer uses smaller per-node footprints (~78–200 px wide,
  // ~40–95 px tall) than Velocity's fixed 240×70. If we copy GeoEvent left/top
  // verbatim, neighboring nodes that were laid out cleanly in GeoEvent will
  // overlap in the Velocity model. To preserve the customer's curated topology
  // we scale all coordinates per service so that Velocity's 240×70 boxes
  // occupy proportionally the same canvas space the GeoEvent nodes did.
  type RawLayout = { left: number; top: number; width: number; height: number };
  const allLayouts: RawLayout[] = [];
  for (const i of service.inputs) if (i.layout) allLayouts.push(i.layout);
  for (const o of service.outputs) if (o.layout) allLayouts.push(o.layout);
  for (const n of service.nodes) if (n.layout) allLayouts.push(n.layout);

  const median = (xs: number[]): number => {
    const s = [...xs].sort((a, b) => a - b);
    return s.length === 0 ? 0 : s[Math.floor(s.length / 2)] ?? 0;
  };
  const VELOCITY_NODE_W = 240;
  const VELOCITY_NODE_H = 70;
  let xScale = 1;
  let yScale = 1;
  let minLeft = 0;
  let minTop = 0;
  if (allLayouts.length > 0) {
    const widths = allLayouts.map(l => l.width).filter(w => w > 0);
    const heights = allLayouts.map(l => l.height).filter(h => h > 0);
    const medW = widths.length > 0 ? median(widths) : VELOCITY_NODE_W;
    const medH = heights.length > 0 ? median(heights) : VELOCITY_NODE_H;
    // Scale up so a Velocity-sized box at the same proportional position
    // does not overlap its neighbors. Never scale down (cap at 1).
    xScale = Math.max(1, VELOCITY_NODE_W / medW);
    yScale = Math.max(1, VELOCITY_NODE_H / medH);
    minLeft = Math.min(...allLayouts.map(l => l.left));
    minTop = Math.min(...allLayouts.map(l => l.top));
  }

  // Helper: pick GeoEvent UI Designer layout when present (rescaled to
  // Velocity's 240×70 footprint), else fall back to the auto-placed slot.
  const layoutOrFallback = (
    layout: RawLayout | undefined,
    fallbackLeft: number,
  ) => {
    if (layout) {
      return {
        width: VELOCITY_NODE_W,
        height: VELOCITY_NODE_H,
        left: Math.round((layout.left - minLeft) * xScale),
        top: Math.round((layout.top - minTop) * yScale),
      };
    }
    return {
      width: VELOCITY_NODE_W,
      height: VELOCITY_NODE_H,
      left: fallbackLeft,
      top: topPosition,
    };
  };

  for (const svcInput of service.inputs) {
    const globalInput = config.inputs.find(i => i.name === svcInput.inputRef);
    if (globalInput) {
      const feedId = idMap.get(globalInput.name) || generateVelocityId();
      feeds.push({
        id: feedId,
        label: sanitizeVelocityLabel(svcInput.label || globalInput.label || globalInput.name).sanitized,
        name: getFeedTypeName(globalInput, targetPlatform),
        properties: {
          ui: layoutOrFallback(svcInput.layout, currentLeft),
        },
      });
      if (!svcInput.layout) currentLeft += 310;
    }
  }
  
  // Build tools array from both processors and filters
  // Data-source types (e.g., FieldEnricher-FeatureService) go to sources, not tools
  const tools: AnalyticTool[] = [];
  const sources: AnalyticSource[] = [];
  const toolIdMap = new Map<string, string>();
  const toolNameMap = new Map<string, string>(); // nodeId → velocity tool name
  // Route by Expressions wiring: choice tool id → (target ref → ordered route
  // slots). Each slot carries the route `id` (used as the pipeline `fromPort`)
  // and a reference to the route/otherwise object whose `destinationId` is
  // back-filled once downstream component ids are resolved during pipeline
  // building. A list per target ref lets multiple clauses fan out to the same
  // destination without colliding.
  const choiceWiring = new Map<string, Map<string, Array<{ id: string; route: { destinationId: string } }>>>();
  currentLeft = 60;
  
  // Resolve the GeoEvent definition once for the service — used for arcadeSampleValues
  const serviceGeoDef = resolveGeoDefForService(service, config);
  
  for (const node of service.nodes) {
    const toolId = uuidv4();
    toolIdMap.set(node.name, toolId);
    
    let toolName: string;
    let toolProps: Record<string, unknown>;
    
    if (node.processor) {
      toolName = resolveToolName(node.processor.uri, matrix);
      toolNameMap.set(node.name, toolName);
      
      // Check if this processor maps to a data-source type
      const matrixEntry = lookupProcessorMapping(matrix, node.processor.uri);
      if (matrixEntry?.velocityType === 'data-source') {
        // Create as a source (feature-layer) instead of a tool
        const processorProps = node.processor.properties;
        const layerId = Number(processorProps['layer']?.value) || 0;
        const fieldsStr = String(processorProps['fields']?.value || '');
        const fieldNames = fieldsStr ? fieldsStr.split(',').map(f => f.trim()).filter(Boolean) : [];
        
        // Build schemaTransformation with attributes from the enrichment fields
        const attributes = fieldNames.map(name => ({
          name,
          dataType: 'String',
          nullable: true,
          tags: [] as string[],
        }));
        // Add standard objectid field if not present
        if (!fieldNames.some(f => f.toLowerCase() === 'objectid')) {
          attributes.unshift({
            name: 'objectid',
            dataType: 'Int64',
            nullable: false,
            tags: ['OBJECT_ID'],
          });
        }
        const fieldMappings = attributes.map(attr => ({
          fromField: attr.name,
          toField: attr.name,
          tags: attr.tags,
        }));
        
        const sourceLabel = sanitizeVelocityLabel(node.label || node.name).sanitized;

        // portalItemId cannot be resolved from GeoEvent data store GUIDs — warn the user
        warnings.push(
          `Data source "${sourceLabel}": portalItemId is empty. Register the enrichment ` +
          `feature layer as a Portal item and set the portalItemId property before import.`
        );

        sources.push({
          id: toolId,
          name: 'feature-layer',
          label: sourceLabel,
          schemaTransformation: {
            inputSchema: {
              attributes,
            },
            fieldMappings,
          },
          properties: {
            'feature-layer.portalItemId': '',
            'feature-layer.outSR': '4326',
            'feature-layer.query': '1=1',
            'feature-layer.fields': fieldsStr || '*',
            'feature-layer.layerId': layerId,
            ui: layoutOrFallback(node.layout, currentLeft),
          },
        });
        if (!node.layout) currentLeft += 310;
        continue;
      }
      
      toolProps = getToolProperties(
        toolName,
        node.processor.properties,
        { left: currentLeft, top: topPosition },
        serviceGeoDef,
        config.geoEventDefinitions,
        matrixEntry?.arcadeAlternative,
        flattenNestedSchema,
      );

      // Choice routers carry their ordered when-clauses as choiceRoutes.
      // Emit one Arcade-converted route expression per clause so the Route by
      // Expressions tool reproduces the choice's serial if/else behavior. This
      // overrides the single placeholder route getToolProperties emits when the
      // node has no GeoEvent routeExpressions property (choice nodes never do).
      if (toolName === 'route-by-expressions' && node.choiceRoutes?.length) {
        const built = buildChoiceRoutingExpressions(node, {
          doFlatten: flattenNestedSchema && hasNestedOrArrayFields(serviceGeoDef),
        });
        const rbeProps = toolProps as Record<string, unknown>;
        // Replace the getToolProperties placeholder with the real per-clause
        // routes. Each expression carries the route id/order Velocity needs.
        rbeProps[`${toolName}.routingExpressions`] = { expressions: built.expressions };
        // A GeoEvent choice `<otherwise>` maps to Velocity's native Otherwise
        // output (unmatched records are routed rather than dropped) — emit the
        // Otherwise route object instead of a synthetic catch-all expression.
        if (built.otherwise) {
          rbeProps[`${toolName}.otherwise`] = built.otherwise;
        }
        // Defer destinationId + pipeline fromPort wiring until downstream
        // component ids are known (outputs/tools are resolved after this loop).
        choiceWiring.set(toolId, built.wiring);
      }
    } else if (node.filter) {
      // Convert GeoEvent filters to Velocity filter-by-expression tools
      toolName = 'filter-by-expression';
      toolNameMap.set(node.name, toolName);
      
      // Build Arcade expression hint from GeoEvent filter conditions
      const filterCtx: FilterArcadeContext = {
        doFlatten: flattenNestedSchema && hasNestedOrArrayFields(serviceGeoDef),
      };
      const filterLogicalCondition = node.filter.logicalCondition as unknown as { conditions?: Array<Record<string, unknown>> };
      if (logicalConditionUsesRegex(filterLogicalCondition)) {
        warnings.push(
          `Filter "${node.label}": the source configuration uses regular-expression matching (the MATCHES operator), ` +
          `which Velocity and Arcade do not natively support at this time. The generated Filter by Expression uses a ` +
          `best-effort Arcade text-function approximation that must be verified; for patterns that cannot be approximated, ` +
          `apply the regular expression upstream before ingest or through an external service. This filter is reported as partially compatible.`
        );
      }
      const arcadeHint = buildArcadeHintFromFilter(node, filterCtx);
      toolProps = {
        ui: layoutOrFallback(node.layout, currentLeft),
        arcadeSampleValues: buildArcadeSampleValues(serviceGeoDef),
        [`${toolName}.expression`]: arcadeHint,
      };
    } else {
      toolName = 'field-calculator';
      toolNameMap.set(node.name, toolName);
      toolProps = {
        ui: layoutOrFallback(node.layout, currentLeft),
        arcadeSampleValues: buildArcadeSampleValues(serviceGeoDef),
      };
    }

    // For processor nodes, getToolProperties has already populated `ui`
    // with the auto-placed slot. Override it with the GeoEvent layout
    // when present so the Velocity Manager opens the model with the
    // same node positions the customer curated in GeoEvent's UI Designer.
    if (node.processor && node.layout) {
      (toolProps as Record<string, unknown>).ui = layoutOrFallback(node.layout, currentLeft);
    }

    tools.push({
      id: toolId,
      name: toolName,
      label: sanitizeVelocityLabel(node.label || node.name).sanitized,
      properties: toolProps,
    });
    if (!node.layout) currentLeft += 310;
  }
  
  // Build outputs array
  // For in-place migrations with feat-lyr-existing outputs, inject a Map Fields tool
  // before each output to convert Int64 fields to Int32 (existing feature layers
  // commonly use esriFieldTypeInteger = Int32, but GeoEvent Integer maps to Velocity Int64).
  const outputs: AnalyticOutput[] = [];
  const outputIdMap = new Map<string, string>();
  // Maps output ID → field-mapper tool ID for pipeline rewiring
  const outputFieldMapperIds = new Map<string, string>();
  
  for (const svcOutput of service.outputs) {
    const globalOutput = config.outputs.find(o => o.name === svcOutput.outputRef);
    if (globalOutput) {
      const outputId = uuidv4();
      outputIdMap.set(globalOutput.name, outputId);
      const outputType = getOutputTypeName(globalOutput, migrationStrategy, targetPlatform);
      
      // For in-place feat-lyr-existing outputs, inject a field-mapper tool
      if (migrationStrategy === 'in-place' && outputType === 'feat-lyr-existing') {
        // Resolve the GeoEvent definition for this service's input to determine field types
        const geoDef = resolveGeoDefForService(service, config);
        if (geoDef) {
          const fieldMapperId = uuidv4();
          const fieldMapperProps = buildInPlaceFieldMapperProperties(
            geoDef,
            { left: currentLeft, top: topPosition }
          );
          tools.push({
            id: fieldMapperId,
            name: 'field-mapper',
            label: 'Map Fields',
            properties: fieldMapperProps,
          });
          toolIdMap.set(`__field-mapper-${globalOutput.name}`, fieldMapperId);
          toolNameMap.set(`__field-mapper-${globalOutput.name}`, 'field-mapper');
          outputFieldMapperIds.set(outputId, fieldMapperId);
          currentLeft += 310;
        }
      }
      
      // Determine formatName for output types that require it (e.g., kafka needs 'json')
      const outputFormatName = outputType === 'kafka'
        ? 'json'
        : outputType === 'write-file'
        ? getWriteFileFormatName(globalOutput)
        : undefined;

      const outputProps = getOutputProperties(
        globalOutput,
        { left: currentLeft, top: topPosition },
        config.arcgisServerConnections,
        migrationStrategy,
        targetPlatform,
        config.folderDataStores,
      );
      // When the GeoEvent XML carries layout coords for this service-output
      // reference, use them so the Velocity model preserves the curated layout.
      if (svcOutput.layout) {
        (outputProps as Record<string, unknown>).ui = layoutOrFallback(svcOutput.layout, currentLeft);
      }

      outputs.push({
        id: outputId,
        name: outputType,
        label: sanitizeVelocityLabel(svcOutput.label || globalOutput.label || globalOutput.name).sanitized,
        ...(outputFormatName ? { formatName: outputFormatName } : {}),
        properties: outputProps,
      });
      if (!svcOutput.layout) currentLeft += 310;
    }
  }
  
  // Track how many connections each tool has received, so we can assign
  // "target" for the first connection and "join" for the second on multi-input tools.
  const toolIncomingCount = new Map<string, number>();
  
  // Build pipeline connections with correct port naming
  const pipeline: PipelineConnection[] = [];
  
  for (const flow of service.flow) {
    const fromRef = flow.fromRef;
    let fromId: string | undefined;
    let fromPort = '';
    
    // Check if from is an input
    const inputMatch = service.inputs.find(i => i.inputRef === fromRef);
    if (inputMatch) {
      const globalInput = config.inputs.find(i => i.name === inputMatch.inputRef);
      if (globalInput) {
        fromId = idMap.get(globalInput.name);
      }
    }
    
    // Check if from is a node (processor/filter)
    if (!fromId) {
      fromId = toolIdMap.get(fromRef);
      // From ports on tools are empty (implicit default output)
    }
    
    if (fromId) {
      const tos: PipelineEndpoint[] = flow.toRefs.map(toRef => {
        let toId: string | undefined;
        let port = '';
        
        // Check if to is an output
        const outputMatch = service.outputs.find(o => o.outputRef === toRef.ref);
        if (outputMatch) {
          const globalOutput = config.outputs.find(o => o.name === outputMatch.outputRef);
          if (globalOutput) {
            toId = outputIdMap.get(globalOutput.name);
            if (toId) {
              // If a field-mapper was injected before this output, redirect to it
              const fieldMapperId = outputFieldMapperIds.get(toId);
              if (fieldMapperId) {
                port = `port-in-${fieldMapperId}`;
                toId = fieldMapperId;
              } else {
                port = `port-in-${toId}`;
              }
            }
          }
        }
        
        // Check if to is a node (processor/filter)
        if (!toId) {
          toId = toolIdMap.get(toRef.ref);
          if (toId) {
            const targetToolName = toolNameMap.get(toRef.ref) || '';
            
            if (MULTI_INPUT_TOOLS.has(targetToolName)) {
              // Multi-input tools use named ports: first connection = "target", subsequent = "join"
              const count = toolIncomingCount.get(toId) || 0;
              port = count === 0 ? 'target' : 'join';
              toolIncomingCount.set(toId, count + 1);
            } else {
              // Single-input tools use port-in-{id}
              port = `port-in-${toId}`;
            }
          }
        }
        
        // Route by Expressions fans out through named route ports. Match this
        // destination to its choice clause so Velocity knows which records to
        // send here: stamp the connection with the route id (`fromPort`) and
        // back-fill the route's destinationId with the resolved component id.
        const endpointProps: Record<string, unknown> = {};
        const wiring = choiceWiring.get(fromId);
        if (wiring && toId) {
          const slots = wiring.get(toRef.ref);
          if (slots && slots.length > 0) {
            const slot = slots.shift()!;
            slot.route.destinationId = toId;
            endpointProps.fromPort = slot.id;
          }
        }
        
        return {
          id: toId || '',
          port,
          properties: endpointProps,
        };
      }).filter(to => to.id);
      
      if (tos.length > 0) {
        pipeline.push({
          from: {
            id: fromId,
            port: fromPort,
            properties: {},
          },
          tos,
        });
      }
    }
  }
  
  // Add field-mapper → output pipeline connections for in-place injected tools
  for (const [outputId, fieldMapperId] of outputFieldMapperIds) {
    pipeline.push({
      from: {
        id: fieldMapperId,
        port: '',
        properties: {},
      },
      tos: [{
        id: outputId,
        port: `port-in-${outputId}`,
        properties: {},
      }],
    });
  }
  
  const analyticLabel = sanitizeVelocityLabel(service.label || service.name).sanitized;

  // RATs with non-empty sources[] fail to import via bulk import (ratsMapped=0, no error).
  // Warn the user so they know to create these manually in Velocity Manager.
  if (sources.length > 0) {
    warnings.push(
      `Real-Time Analytic "${analyticLabel}" contains ${sources.length} data source(s) ` +
      `(feature-layer enrichment). The bulk import API silently fails for RATs with data ` +
      `sources. Create the data source(s) manually in the Velocity Manager, then import ` +
      `the RAT without the sources array, or create the entire RAT manually.`
    );
  }

  return {
    id: analyticId,
    label: analyticLabel,
    description: service.description || '',
    sources,
    feeds,
    tools,
    outputs,
    pipeline,
    properties: {
      smartLayoutMode: '',
      executable: true,
      folderName: null,
    },
    shareProperties: false,
  };
}

/**
 * Converts a single AttributeCondition to an Arcade expression fragment.
 */
function sanitizeArcadeCommentValue(value: string): string {
  return value.replace(/\*\//g, '* /').replace(/[\r\n]+/g, ' ');
}

interface FilterArcadeContext {
  /** When true, nested/array operand paths are flattened to their feed field names. */
  doFlatten: boolean;
}

function attributeConditionToArcade(
  condition: { operand: string; operator: string; value: string },
  ctx: FilterArcadeContext,
): string {
  const field = operandToArcadeFieldRef(condition.operand, ctx.doFlatten);
  const val = condition.value;
  // Treat a non-empty, numeric-looking value as a number so comparisons against
  // numeric fields are not emitted as string literals (e.g. `== 0.0`, not
  // `== "0.0"`, which would never match a numeric field). An empty string is
  // NOT numeric even though Number('') is 0.
  const isNumericValue = val.trim() !== '' && !isNaN(Number(val));

  // Helper for IN/NOT IN: parse comma-separated list (with optional surrounding parens)
  // and emit equality (single value) or Includes() (multiple values).
  const renderInList = (negate: boolean): string => {
    const stripped = val.trim().replace(/^\(|\)$/g, '');
    const items = stripped
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((raw) => {
        const sq = raw.match(/^'(.*)'$/);
        if (sq) return JSON.stringify(sq[1]);
        const dq = raw.match(/^"(.*)"$/);
        if (dq) return JSON.stringify(dq[1]);
        return isNaN(Number(raw)) ? JSON.stringify(raw) : raw;
      });
    if (items.length === 0) {
      return negate ? `${field} != null` : `${field} == null`;
    }
    if (items.length === 1) {
      return `${field} ${negate ? '!=' : '=='} ${items[0]}`;
    }
    return `${negate ? '!' : ''}Includes([${items.join(', ')}], ${field})`;
  };

  switch (condition.operator.toUpperCase()) {
    case '=':
      return isNumericValue ? `${field} == ${val}` : `${field} == ${JSON.stringify(val)}`;
    case '!=':
      return isNumericValue ? `${field} != ${val}` : `${field} != ${JSON.stringify(val)}`;
    case '<':
    case '>':
    case '<=':
    case '>=':
      // Use numeric comparison if value is numeric, otherwise string
      return isNumericValue
        ? `${field} ${condition.operator} ${val}`
        : `${field} ${condition.operator} ${JSON.stringify(val)}`;
    case 'IN':
      return renderInList(false);
    case 'NOT IN':
      return renderInList(true);
    case 'EXISTS':
    case 'IS NOT NULL':
      return `!IsEmpty(${field})`;
    case 'NOT EXISTS':
    case 'IS NULL':
      return `IsEmpty(${field})`;
    case 'ISNULL':
      return `${field} == null`;
    case 'NOTNULL':
      return `${field} != null`;
    case 'CONTAINS':
      return `Includes(${field}, ${JSON.stringify(val)})`;
    case 'STARTS_WITH':
      return `Left(${field}, ${val.length}) == ${JSON.stringify(val)}`;
    case 'ENDS_WITH':
      return `Right(${field}, ${val.length}) == ${JSON.stringify(val)}`;
    case 'MATCHES': {
      // Velocity/Arcade has no native regular-expression support. Translate the
      // pattern to an Arcade text-function approximation when it fits the
      // supported bounded grammar; otherwise emit an honest note with real
      // alternatives rather than a misleading "rewrite as Arcade" instruction.
      const translated = translateRegexMatchToArcade(field, val);
      if (translated) {
        return `(${translated.expression})`;
      }
      return `/* ACTION REQUIRED: Velocity and Arcade have no native regular-expression support, and this pattern could not be auto-approximated. GeoEvent MATCHES for ${sanitizeArcadeCommentValue(condition.operand)}: ${sanitizeArcadeCommentValue(JSON.stringify(val))}. Options: apply the pattern upstream before ingest, route through an external service, or hand-build an Arcade text-function approximation. */ false`;
    }
    default:
      return `/* ACTION REQUIRED: Unsupported operator "${sanitizeArcadeCommentValue(condition.operator)}" for ${sanitizeArcadeCommentValue(condition.operand)} - convert manually */ null`;
  }
}

/**
 * Recursively converts a LogicalCondition tree to an Arcade expression.
 */
function logicalConditionToArcade(
  cond: { operator: string; conditions: Array<Record<string, unknown>> },
  ctx: FilterArcadeContext,
): string {
  const parts: string[] = [];

  for (const child of cond.conditions) {
    if ('operand' in child) {
      // AttributeCondition
      parts.push(attributeConditionToArcade(child as { operand: string; operator: string; value: string }, ctx));
    } else if ('conditions' in child) {
      // Nested LogicalCondition
      const nested = logicalConditionToArcade(child as { operator: string; conditions: Array<Record<string, unknown>> }, ctx);
      // Wrap nested groups in parentheses when mixing with other conditions
      parts.push(parts.length > 0 || cond.conditions.length > 1 ? `(${nested})` : nested);
    }
  }

  if (parts.length === 0) {
    return '';
  }

  const operator = cond.operator.toUpperCase();
  if (operator === 'NOT') {
    return `!(${parts.length === 1 ? parts[0] : parts.join(' || ')})`;
  }

  const joiner = operator === 'AND' ? ' && ' : ' || ';
  return parts.join(joiner);
}

/**
 * Builds an Arcade expression hint from a GeoEvent filter's logical conditions.
 * Handles all FilterOperator types including CONTAINS, STARTS_WITH, ENDS_WITH,
 * comparison operators, and nested logical conditions.
 */
function buildArcadeHintFromFilter(node: ServiceNode, ctx: FilterArcadeContext): string {
  if (!node.filter?.logicalCondition) {
    return '/* ACTION REQUIRED: Convert GeoEvent filter to Arcade expression.\n' +
      '   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/\n' +
      '   Velocity Arcade guide: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm */ null';
  }

  const logicalCondition = node.filter.logicalCondition as unknown as { operator: string; conditions: Array<Record<string, unknown>> };
  const expr = logicalConditionToArcade(logicalCondition, ctx);

  if (!expr) {
    return '/* ACTION REQUIRED: Convert GeoEvent filter to Arcade expression.\n' +
      '   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/\n' +
      '   Velocity Arcade guide: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm */ null';
  }

  // GeoEvent regex matching has no native Velocity/Arcade equivalent; when the
  // filter used MATCHES, prepend a single VERIFY header explaining that the
  // expression is a text-function approximation to confirm (or replace with
  // upstream handling).
  if (logicalConditionUsesRegex(logicalCondition)) {
    return '/* VERIFY: This filter used GeoEvent regular-expression matching, which Velocity and Arcade do not support natively.\n' +
      '   The expression below approximates the pattern(s) with Arcade text functions and assumes full-match semantics.\n' +
      '   Confirm the behavior, or apply the pattern upstream before ingest for exact regex matching. */\n' +
      expr;
  }

  return expr;
}

/** True when any AttributeCondition in the tree uses the regex MATCHES operator. */
function logicalConditionUsesRegex(cond: { conditions?: Array<Record<string, unknown>> }): boolean {
  for (const child of cond.conditions ?? []) {
    if ('operator' in child && typeof child.operator === 'string' && child.operator.toUpperCase() === 'MATCHES') {
      return true;
    }
    if ('conditions' in child && logicalConditionUsesRegex(child as { conditions?: Array<Record<string, unknown>> })) {
      return true;
    }
  }
  return false;
}

/**
 * A single Velocity Route by Expressions route. `id` is a stable route port
 * identifier referenced by the pipeline connection's `fromPort`; `destinationId`
 * is the resolved id of the component this route feeds and is back-filled
 * during pipeline wiring once downstream ids are known.
 */
interface VelocityRouteExpression {
  label: string;
  id: string;
  expression: string;
  order: number;
  destinationId: string;
}

/** Velocity Route by Expressions native Otherwise (catch-all) route. */
interface VelocityOtherwiseRoute {
  id: string;
  destinationId: string;
  label: string;
}

/**
 * Builds the Route by Expressions routing structure for a synthetic choice
 * node. Each ordered `<when>` clause becomes a route with an Arcade-converted
 * expression (clauses that cannot be auto-converted emit an ACTION REQUIRED
 * placeholder), a stable route `id`, and a 0-based `order`; a `<otherwise>`
 * clause becomes Velocity's native Otherwise route. Destination ids are left
 * empty here and back-filled during pipeline wiring via the returned `wiring`
 * map (targetRef → ordered route slots), which also supplies each connection's
 * `fromPort`.
 */
function buildChoiceRoutingExpressions(
  node: ServiceNode,
  ctx: FilterArcadeContext,
): {
  expressions: VelocityRouteExpression[];
  otherwise?: VelocityOtherwiseRoute;
  wiring: Map<string, Array<{ id: string; route: { destinationId: string } }>>;
} {
  const expressions: VelocityRouteExpression[] = [];
  let otherwise: VelocityOtherwiseRoute | undefined;
  const wiring = new Map<string, Array<{ id: string; route: { destinationId: string } }>>();

  const addWiring = (targetRef: string, id: string, route: { destinationId: string }) => {
    if (!targetRef) return;
    const slots = wiring.get(targetRef) ?? [];
    slots.push({ id, route });
    wiring.set(targetRef, slots);
  };

  if (!node.choiceRoutes?.length) return { expressions, otherwise, wiring };

  const ACTION_REQUIRED =
    '/* ACTION REQUIRED: Convert GeoEvent choice condition to Arcade expression.\n' +
    '   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/\n' +
    '   Velocity Arcade guide: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm */ null';

  [...node.choiceRoutes]
    .filter((route) => !route.isOtherwise)
    .sort((a, b) => a.order - b.order)
    .forEach((route, idx) => {
      const expr = logicalConditionToArcade(
        route.condition as unknown as {
          operator: string;
          conditions: Array<Record<string, unknown>>;
        },
        ctx,
      );
      const routeObj: VelocityRouteExpression = {
        label: route.label || route.targetLabel || route.name,
        id: uuidv4(),
        expression: expr || ACTION_REQUIRED,
        order: idx,
        destinationId: '',
      };
      expressions.push(routeObj);
      addWiring(route.targetRef, routeObj.id, routeObj);
    });

  const otherwiseRoute = node.choiceRoutes.find((route) => route.isOtherwise);
  if (otherwiseRoute) {
    otherwise = {
      id: uuidv4(),
      destinationId: '',
      label: otherwiseRoute.label || 'Otherwise',
    };
    addWiring(otherwiseRoute.targetRef, otherwise.id, otherwise);
  }

  return { expressions, otherwise, wiring };
}

/**
 * Validates the generated Velocity components for the target platform,
 * surfacing structural issues (such as feeds with unknown types) that the
 * user must resolve manually before deployment.
 */
function validateForPlatform(
  feedItems: VelocityFeedItem[],
  _analytics: VelocityRealtimeAnalytic[],
  _platform: TargetPlatform
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check for unknown feed types
  for (const feed of feedItems) {
    if (!feed.feed.name) {
      errors.push(`Feed "${feed.label}" has unknown type - manual configuration required`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Generates Velocity JSON configuration from GeoEvent configuration.
 *
 * @param matrix - Pre-loaded functional matrix. Browser callers MUST pass
 *   this; Node callers may omit it and the matrix will be lazy-loaded
 *   from disk (legacy behavior).
 */
export function generateVelocityJson(
  geoEventConfig: GeoEventConfiguration,
  targetPlatform: TargetPlatform,
  migrationStrategy: MigrationStrategy = 'side-by-side',
  securedInputNames: Set<string> = new Set(),
  matrix?: LoadedFunctionalMatrix,
  options: GenerationOptions = {},
): GenerationResult {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Nested Group fields and Many-cardinality arrays are flattened at the feed
  // level by default (see GenerationOptions.flattenNestedSchema).
  const flattenNestedSchema = options.flattenNestedSchema !== false;

  // Get the functional matrix
  const resolvedMatrix = getMatrix(matrix);

  // Map all components
  const mappingResult = mapConfiguration(geoEventConfig, resolvedMatrix, targetPlatform, migrationStrategy, securedInputNames);

  // ID mapping for cross-referencing
  const idMap = new Map<string, string>();

  // Track which inputs have been emitted into the JSON. Both fully-mapped
  // inputs and partial-mapping inputs are included so that users can import
  // the rest of the analytic model and only have to fix the broken feed
  // (e.g. swap in a Portal item that wraps a secured or AGOL-hosted
  // feature service). Only `unmapped` inputs — which have no Velocity
  // equivalent at all — are skipped.
  const includedInputNames = new Set<string>();

  // Generate feed items
  const feedItems: VelocityFeedItem[] = [];
  for (const input of geoEventConfig.inputs) {
    const mapping = mappingResult.mappings.find(
      (m) => 'adapter' in m.component && m.component.name === input.name
    );

    if (mapping?.status === 'unmapped') {
      warnings.push(`Input "${input.label}" could not be mapped - requires manual configuration`);
      continue;
    }

    if (mapping?.status === 'partial') {
      warnings.push(
        `Input "${input.label}" has partial mapping — feed has been generated so the rest of ` +
        `the configuration can be imported, but this feed will not run until the manual steps ` +
        `noted in the migration guide are completed (typically wrapping a secured or ArcGIS ` +
        `Online–hosted service in a Portal item with stored credentials, then rebinding the feed).`
      );
    }

    includedInputNames.add(input.name);

    // Find matching GeoEvent definition — try all property name variants
    // against actual definitions. File adapters use "ExistingFixedGeoEventDefinitionName" /
    // "NewFixedGeoEventDefinitionName", other adapters use "ExistingGeoEventDefinitionName" /
    // "NewGeoEventDefinitionName". The first non-empty value may reference a definition that
    // doesn't exist (e.g. "incident" when only "Wildfire" is defined), so try each candidate.
    const ap = input.adapter?.properties;
    const defCandidates = [
      ap?.['ExistingFixedGeoEventDefinitionName']?.value,
      ap?.['ExistingGeoEventDefinitionName']?.value,
      ap?.['NewFixedGeoEventDefinitionName']?.value,
      ap?.['NewGeoEventDefinitionName']?.value,
    ].filter((v): v is string => typeof v === 'string' && v.length > 0);
    let geoDef: (typeof geoEventConfig.geoEventDefinitions)[number] | undefined;
    for (const candidate of defCandidates) {
      geoDef = geoEventConfig.geoEventDefinitions.find(d => d.name === candidate);
      if (geoDef) break;
    }

    feedItems.push(inputToVelocityFeed(input, geoDef, idMap, geoEventConfig.arcgisServerConnections, targetPlatform, geoEventConfig.folderDataStores, warnings, geoEventConfig, flattenNestedSchema));
  }

  // Generate realtime analytics from services. An analytic is included
  // whenever at least one of its referenced inputs made it into the JSON
  // (mapped or partial). Partial-input analytics are still useful — the
  // user only needs to rebind the broken feed in the analytic model.
  const realtimeAnalytics: VelocityRealtimeAnalytic[] = [];
  for (const service of geoEventConfig.geoEventServices) {
    const hasIncludedInput = service.inputs.some(
      si => includedInputNames.has(si.inputRef)
    );
    if (hasIncludedInput) {
      realtimeAnalytics.push(serviceToRealtimeAnalytic(service, geoEventConfig, idMap, mappingResult, resolvedMatrix, targetPlatform, migrationStrategy, warnings, flattenNestedSchema));
    }
  }

  // If no fully compatible components exist, don't generate a JSON configuration
  if (feedItems.length === 0 && realtimeAnalytics.length === 0) {
    return {
      success: true,
      configuration: null,
      mappingResult,
      warnings: warnings.map(redactSensitiveText),
      errors: errors.map(redactSensitiveText),
    };
  }

  // Validate for target platform
  const validation = validateForPlatform(feedItems, realtimeAnalytics, targetPlatform);
  if (!validation.valid) {
    errors.push(...validation.errors);
  }

  let redactedAnyArtifact = false;
  const redactedFeedItems = feedItems.map((feed) => {
    const result = redactGeneratedArtifact(feed);
    redactedAnyArtifact ||= result.changed;
    return result.artifact;
  });
  const redactedRealtimeAnalytics = realtimeAnalytics.map((analytic) => {
    const result = redactGeneratedArtifact(analytic);
    redactedAnyArtifact ||= result.changed;
    return result.artifact;
  });
  addRedactionWarningIfNeeded(warnings, redactedAnyArtifact);

  // Build the configuration in Velocity backup format
  const configuration: VelocityConfiguration = {
    orgId: '0123456789ABCDEF', // Default ArcGIS Portal org ID
    timestamp: Date.now(),
    feeds: redactedFeedItems.map(f => JSON.stringify(f)),
    realtime: redactedRealtimeAnalytics.map(a => JSON.stringify(a)),
  };

  return {
    success: errors.length === 0,
    configuration,
    mappingResult,
    warnings: warnings.map(redactSensitiveText),
    errors: errors.map(redactSensitiveText),
  };
}

/**
 * Serializes Velocity configuration to JSON string
 */
export function serializeConfiguration(config: VelocityConfiguration): string {
  return JSON.stringify(redactSensitiveObject(config), null, 2);
}

// =============================================================================
// Standalone generation functions (Phase 2)
// These accept direct parameters — no GeoEvent Input/Output objects needed.
// =============================================================================

/**
 * Schema field definition for standalone generation
 */
export interface StandaloneSchemaField {
  name: string;
  dataType: 'String' | 'Int32' | 'Int64' | 'Float32' | 'Float64' | 'Date' | 'Boolean';
  nullable?: boolean;
  tags?: string[];
}

/**
 * Geometry definition for standalone generation
 */
export interface StandaloneGeometry {
  geometryType?: 'esriGeometryPoint' | 'esriGeometryPolyline' | 'esriGeometryPolygon' | 'esriGeometryMultipoint';
  wkid?: number;
  hasZ?: boolean;
}

/**
 * Feed configuration for standalone generation
 */
export interface StandaloneFeedParams {
  feedType: string;
  label: string;
  properties: Record<string, unknown>;
  schema?: StandaloneSchemaField[];
  geometry?: StandaloneGeometry;
  formatName?: string;
  recurrence?: { expression: string; timeZone: string };
}

/**
 * Output configuration for standalone generation
 */
export interface StandaloneOutputParams {
  outputType: string;
  label: string;
  properties: Record<string, unknown>;
  formatName?: string;
}

/**
 * Tool configuration for standalone generation
 */
export interface StandaloneToolParams {
  toolName: string;
  label: string;
  properties: Record<string, unknown>;
}

/**
 * Pipeline connection for standalone generation
 */
export interface StandalonePipelineConnection {
  fromId: string;
  fromPort?: string;
  toId: string;
  toPort: string;
}

/**
 * Full pipeline description for standalone generation
 */
export interface StandaloneFullConfigParams {
  label: string;
  description?: string;
  feeds: StandaloneFeedParams[];
  tools: StandaloneToolParams[];
  outputs: StandaloneOutputParams[];
  pipeline: StandalonePipelineConnection[];
  orgId?: string;
}

/**
 * Generates a standalone Velocity feed configuration from direct parameters.
 * Does not require a GeoEvent Input object.
 */
export function generateStandaloneFeed(params: StandaloneFeedParams): VelocityFeedItem {
  const feedId = generateVelocityId();
  const { sanitized: label } = sanitizeVelocityLabel(params.label);

  // Build schema attributes from provided fields
  const attributes: SchemaAttribute[] = (params.schema ?? []).map(field => ({
    name: field.name,
    dataType: field.dataType,
    nullable: field.nullable ?? true,
    tags: field.tags ?? [],
  }));

  // Build field mappings (1:1 from name to name)
  const fieldMappings: FieldMapping[] = (params.schema ?? []).map(field => ({
    fromField: field.name,
    toField: field.name,
    tags: field.tags ?? [],
  }));

  // Build geometry
  let geometry: SchemaGeometry | undefined;
  if (params.geometry) {
    geometry = {
      geometryType: params.geometry.geometryType ?? 'esriGeometryPoint',
      spatialReference: { wkid: params.geometry.wkid ?? 4326 },
      hasZ: params.geometry.hasZ ?? false,
      hasM: false,
    };
  }

  // Detect time fields — include time metadata when START_TIME/END_TIME tags exist
  // or when any Date attribute is present (Velocity expects time metadata for temporal data)
  const hasTimeField = (params.schema ?? []).some(
    f => f.tags?.some(t => t === 'START_TIME' || t === 'END_TIME')
  );
  const hasDateAttribute = attributes.some(a => a.dataType === 'Date');

  const schemaTransformation = attributes.length > 0 ? {
    inputSchema: {
      attributes,
      geometry,
      ...((hasTimeField || hasDateAttribute) ? { time: { timeType: 'instant' } } : {}),
    },
    fieldMappings,
  } : undefined;

  // Namespace the properties with feed type prefix
  const namespacedProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params.properties)) {
    // If key already has a dot (namespaced), keep as-is; otherwise prefix
    namespacedProps[key.includes('.') ? key : `${params.feedType}.${key}`] = value;
  }

  const recurrence = params.recurrence ? {
    expression: params.recurrence.expression,
    timeZone: params.recurrence.timeZone,
    suspend: false,
  } : {
    expression: '0/10 * * ? * * *',
    timeZone: 'America/New_York',
    suspend: false,
  };

  return redactSensitiveObject({
    id: feedId,
    label,
    description: '',
    feed: {
      name: params.feedType,
      formatName: params.formatName,
      schemaTransformation,
      properties: namespacedProps,
    },
    recurrence,
    properties: {
      executable: true,
      folderName: null,
    },
    shareProperties: false,
  });
}

/**
 * Generates a standalone Velocity output configuration from direct parameters.
 */
export function generateStandaloneOutput(params: StandaloneOutputParams): AnalyticOutput {
  const outputId = generateVelocityId();
  const { sanitized: label } = sanitizeVelocityLabel(params.label);

  // Namespace properties
  const namespacedProps: Record<string, unknown> = {
    ui: { width: 240, height: 70, left: 680, top: 70 },
  };
  for (const [key, value] of Object.entries(params.properties)) {
    namespacedProps[key.includes('.') ? key : `${params.outputType}.${key}`] = value;
  }

  return redactSensitiveObject({
    id: outputId,
    name: params.outputType,
    label,
    ...(params.formatName ? { formatName: params.formatName } : {}),
    properties: namespacedProps,
  });
}

/**
 * Generates a standalone Velocity tool configuration from direct parameters.
 */
export function generateStandaloneTool(params: StandaloneToolParams): AnalyticTool {
  const toolId = generateVelocityId();
  const { sanitized: label } = sanitizeVelocityLabel(params.label);

  // Namespace properties
  const namespacedProps: Record<string, unknown> = {
    ui: { width: 240, height: 70, left: 370, top: 70 },
    arcadeSampleValues: {},
  };
  for (const [key, value] of Object.entries(params.properties)) {
    namespacedProps[key.includes('.') ? key : `${params.toolName}.${key}`] = value;
  }

  return redactSensitiveObject({
    id: toolId,
    name: params.toolName,
    label,
    properties: namespacedProps,
  });
}

/**
 * Generates a full Velocity importable configuration from standalone components.
 * Combines feeds, tools, outputs, and pipeline connections into a VelocityConfiguration.
 */
export function generateFullConfig(params: StandaloneFullConfigParams): {
  configuration: VelocityConfiguration;
  warnings: string[];
} {
  const warnings: string[] = [];
  const orgId = params.orgId ?? '0123456789ABCDEF';

  // Generate feeds
  const feedItems: VelocityFeedItem[] = params.feeds.map(f => generateStandaloneFeed(f));

  // Build ID lookup: positional label → actual generated ID
  const feedIdMap = new Map<number, string>();
  feedItems.forEach((fi, idx) => feedIdMap.set(idx, fi.id));

  // Generate tools
  const toolItems: AnalyticTool[] = params.tools.map((t, idx) => {
    const tool = generateStandaloneTool(t);
    // Position tools horizontally
    (tool.properties as Record<string, unknown>).ui = {
      width: 240, height: 70,
      left: 60 + idx * 310, top: 70,
    };
    return tool;
  });
  const toolIdMap = new Map<number, string>();
  toolItems.forEach((ti, idx) => toolIdMap.set(idx, ti.id));

  // Generate outputs
  const outputItems: AnalyticOutput[] = params.outputs.map((o, idx) => {
    const output = generateStandaloneOutput(o);
    (output.properties as Record<string, unknown>).ui = {
      width: 240, height: 70,
      left: 60 + (params.tools.length + idx) * 310, top: 70,
    };
    return output;
  });
  const outputIdMap = new Map<number, string>();
  outputItems.forEach((oi, idx) => outputIdMap.set(idx, oi.id));

  // Build pipeline connections
  // Pipeline uses component IDs. Caller provides index-based references via fromId/toId.
  // Convention: "feed:0", "tool:0", "output:0" etc.
  const pipeline: PipelineConnection[] = [];
  for (const conn of params.pipeline) {
    const fromId = resolveComponentId(conn.fromId, feedIdMap, toolIdMap, outputIdMap);
    const toId = resolveComponentId(conn.toId, feedIdMap, toolIdMap, outputIdMap);

    if (!fromId || !toId) {
      warnings.push(`Pipeline connection "${conn.fromId}" → "${conn.toId}" could not be resolved`);
      continue;
    }

    // Resolve port — replace index references with actual IDs
    let toPort = conn.toPort;
    if (toPort === 'port-in-auto') {
      toPort = `port-in-${toId}`;
    }

    // Check if there's already a "from" entry for this ID
    const existing = pipeline.find(p => p.from.id === fromId);
    if (existing) {
      existing.tos.push({ id: toId, port: toPort, properties: {} });
    } else {
      pipeline.push({
        from: { id: fromId, port: conn.fromPort ?? '', properties: {} },
        tos: [{ id: toId, port: toPort, properties: {} }],
      });
    }
  }

  // Build analytic feed refs
  const analyticFeeds: AnalyticFeedRef[] = feedItems.map((fi, idx) => ({
    id: fi.id,
    label: fi.label,
    name: fi.feed.name,
    properties: {
      ui: { width: 240, height: 70, top: 70, left: -250 + idx * 310 },
    },
  }));

  const analytic: VelocityRealtimeAnalytic = {
    id: generateVelocityId(),
    label: sanitizeVelocityLabel(params.label).sanitized,
    description: params.description ?? '',
    sources: [],
    feeds: analyticFeeds,
    tools: toolItems,
    outputs: outputItems,
    pipeline,
    properties: {
      smartLayoutMode: '',
      executable: true,
      folderName: null,
    },
    shareProperties: false,
  };

  const redactedFeeds = feedItems.map((feed) => redactGeneratedArtifact(feed));
  const redactedAnalytic = redactGeneratedArtifact(analytic);
  addRedactionWarningIfNeeded(
    warnings,
    redactedAnalytic.changed || redactedFeeds.some((feed) => feed.changed),
  );

  const configuration: VelocityConfiguration = {
    orgId,
    timestamp: Date.now(),
    feeds: redactedFeeds.map(({ artifact }) => JSON.stringify(artifact)),
    realtime: [JSON.stringify(redactedAnalytic.artifact)],
  };

  return { configuration, warnings: warnings.map(redactSensitiveText) };
}

/**
 * Resolves a component reference string like "feed:0", "tool:1", "output:0"
 * to the actual generated ID.
 */
function resolveComponentId(
  ref: string,
  feedIds: Map<number, string>,
  toolIds: Map<number, string>,
  outputIds: Map<number, string>,
): string | undefined {
  const [type, indexStr] = ref.split(':');
  const idx = parseInt(indexStr ?? '', 10);
  if (isNaN(idx)) return ref; // Treat as literal ID
  switch (type) {
    case 'feed': return feedIds.get(idx);
    case 'tool': return toolIds.get(idx);
    case 'output': return outputIds.get(idx);
    default: return ref;
  }
}

/**
 * Returns the list of known feed types and their required properties.
 */
export function getFeedTypeInfo(): Array<{ name: string; requiredProperties: string[] }> {
  return [
    { name: 'feature-layer', requiredProperties: ['url', 'portalItemId', 'layerId'] },
    { name: 'http-poller', requiredProperties: ['url'] },
    { name: 'websocket', requiredProperties: ['url'] },
    { name: 'kafka', requiredProperties: ['brokers', 'topics'] },
    { name: 'mqtt', requiredProperties: ['host', 'port', 'topic'] },
    { name: 'tcp', requiredProperties: ['host', 'port'] },
    { name: 'rss-feed', requiredProperties: ['url'] },
    { name: 'stream-layer', requiredProperties: ['portalItemId'] },
    { name: 'watch-file', requiredProperties: ['fileDirectory'] },
    { name: 'simulator', requiredProperties: ['simulationType'] },
    { name: 'http-receiver', requiredProperties: [] },
  ];
}

/**
 * Returns the list of known output types and their required properties.
 */
export function getOutputTypeInfo(): Array<{ name: string; requiredProperties: string[] }> {
  return [
    { name: 'feat-lyr-new', requiredProperties: ['name'] },
    { name: 'feat-lyr-existing', requiredProperties: ['portalItemId', 'layerId'] },
    { name: 'stream-lyr-new', requiredProperties: ['name'] },
    { name: 'kafka', requiredProperties: ['brokers', 'topics'] },
    { name: 'email', requiredProperties: ['smtpServer', 'fromEmailAddress', 'toEmailAddress'] },
    { name: 'text-message', requiredProperties: ['smtpServer', 'fromAddress', 'toAddress'] },
    { name: 'http-output', requiredProperties: ['url'] },
  ];
}

/**
 * Converts a GeoEvent expression to Arcade.
 */
export { convertGeoEventExpressionToArcade };

/**
 * Validates an Arcade expression for unsupported functions.
 */
export { findUnsupportedArcadeFunctions };

/**
 * Builds Arcade sample values from a GeoEvent definition.
 */
export { buildArcadeSampleValues };

/**
 * Returns the set of multi-input tool names.
 */
export function getMultiInputToolNames(): string[] {
  return Array.from(MULTI_INPUT_TOOLS);
}

/**
 * Exposes getToolProperties for standalone tool configuration generation.
 * Wraps the internal function with sensible defaults.
 */
export function generateToolProperties(
  toolName: string,
  properties: Record<string, unknown>,
): Record<string, unknown> {
  // Convert flat properties to GeoEvent-style property bag
  const processorProps: Record<string, { value: unknown; type: string }> = {};
  for (const [key, value] of Object.entries(properties)) {
    processorProps[key] = { value, type: typeof value === 'string' ? 'String' : 'Unknown' };
  }
  return getToolProperties(toolName, processorProps, { left: 370, top: 70 });
}
